import React, { useState, useEffect, useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  useTheme,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormHelperText,
  FormControlLabel,
  InputAdornment,
  Menu,
  MenuItem as MenuItemComponent,
  useMediaQuery,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Stop as StopIcon,
  Menu as MenuIcon,
  HourglassEmpty as HourglassEmptyIcon,
  TaskAlt as TaskAltIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  Settings as SettingsIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  Campaign as CampaignIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { keyframes, alpha } from '@mui/material';
import { darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { selectLabels } from 'state/dictionary/dictionarySlice';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { Logger } from 'utils/Logger';
import { trackAction } from 'utils/Infralytiqs';
import { handleApiError } from 'utils/ErrorHandler';
import { apiClient } from 'utils/ApiClient';
import { useModulePermissions } from 'hooks/useModulePermissions';
import { utcToLocalDateTime, localDateTimeToUtc, formatUtcToLocal, getCurrentLocalDateTime } from 'utils/TimezoneUtils';
import { DataTableWithStaticSchema } from 'components/DataTable';
import {
  CAMPAIGN_RUNNER_URL,
  CAMPAIGN_RUNNER_INDIVIDUAL_URL,
  TEMPLATE_URL,
  GET_COLLECTION_DATASETS_URL,
  GET_COLLECTION_SCHEMA_URL,
} from 'envConfig';
import { CampaignRunner, CreateCampaignRunnerRequest } from 'modules/Interfaces/InstanUpdates/CampaignRunner';
import { Template } from 'modules/Interfaces/InstanUpdates/Template';
import type { ProductPlan } from 'modules/Interfaces/Company/Company';
import { DataSets } from 'modules/Interfaces/InstanUpdates/DataSets';
import { CountdownTimer } from 'components/CountdownTimer';

interface CampaignWithDetails extends CampaignRunner {
  templateName?: string;
  dataSetName?: string;
  createdByUserName?: string;
}

// CampaignRunner schema for DataTable
const campaignRunnerSchema = {
  paths: {
    _id: { instance: 'String', isRequired: false },
    companyId: { instance: 'String', isRequired: false },
    templateId: { instance: 'String', isRequired: false },
    dataSetId: { instance: 'String', isRequired: false },
    startTimestamp: { instance: 'Date', isRequired: false },
    status: { instance: 'String', isRequired: false },
    timeInterval: { instance: 'Number', isRequired: false },
    createdBy: { instance: 'String', isRequired: false },
    createdOn: { instance: 'Date', isRequired: false },
    completedOn: { instance: 'Date', isRequired: false },
    totalContacts: { instance: 'Number', isRequired: false },
    processedContacts: { instance: 'Number', isRequired: false },
    failedContacts: { instance: 'Number', isRequired: false },
    successContacts: { instance: 'Number', isRequired: false },
    campaignName: { instance: 'String', isRequired: false },
    campaignType: { instance: 'String', isRequired: false },
    description: { instance: 'String', isRequired: false },
    filters: { instance: 'Object', isRequired: false },
    executionLog: { instance: 'Array', isRequired: false },
    executionLogTotalCount: { instance: 'Number', isRequired: false },
    executionLogNonPendingCount: { instance: 'Number', isRequired: false },
    createdAt: { instance: 'Date', isRequired: false },
    updatedAt: { instance: 'Date', isRequired: false },
    templateName: { instance: 'String', isRequired: false },
    dataSetName: { instance: 'String', isRequired: false },
    createdByUserName: { instance: 'String', isRequired: false },
  },
  obj: {}
};

type TimelineOppositeMetaStackProps = {
  startTimestamp: unknown;
  tabId: string;
  /** Same surface as campaign toolbar panel (e.g. toolbarBg) for the date card */
  campaignPanelBg?: (th: any) => string;
  /** Mobile: single column sections inside one parent panel (no separate floating cards) */
  variant?: 'default' | 'unifiedMobile';
};

/** Desktop opposite column: single panel with day, month, year, and start time (no live tick). */
const TimelineOppositeDateOnlyPanel: React.FC<{
  startTimestamp: unknown;
  campaignPanelBg?: (th: any) => string;
  metaAlign: 'start' | 'end';
  /** Status-driven accent color for border, day circle, and text highlights */
  accentColor?: string;
}> = ({ startTimestamp, campaignPanelBg, metaAlign, accentColor }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const d = new Date(parseCampaignStartTimestampMs(startTimestamp));
  const dayNum = d.getDate();
  const monthShort = d.toLocaleDateString('en-US', { month: 'short' }).replace('.', '').toUpperCase();
  const year = d.getFullYear();
  const timeOnly = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const resolvedAccent = accentColor || theme.palette.secondary.main;

  const useCampaignSurface = Boolean(campaignPanelBg);
  const calBgResolved = campaignPanelBg ?? (isDark ? '#212B5E' : theme.palette.grey[100]);

  const rowJustify = metaAlign === 'end' ? 'flex-end' : 'flex-start';
  const timeAlign = metaAlign === 'end' ? 'right' : 'left';

  const circleEl = (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        border: `3px solid ${resolvedAccent}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: resolvedAccent,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1 }}>{dayNum}</Typography>
    </Box>
  );

  const monthYearBlock = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: metaAlign === 'end' ? 'flex-end' : 'flex-start',
        justifyContent: 'center',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '1.15rem',
          letterSpacing: 2,
          lineHeight: 1.2,
          color: resolvedAccent,
        }}
      >
        {monthShort}
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '1rem',
          mt: 0.25,
          color: resolvedAccent,
          opacity: 0.85,
        }}
      >
        {year}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: 300,
      }}
    >
      <Box
        sx={{
          bgcolor: calBgResolved,
          borderRadius: 2,
          p: 1.5,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          boxShadow: useCampaignSurface ? 1 : isDark ? 4 : 1,
          border: '1px solid',
          borderColor: resolvedAccent,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
            justifyContent: rowJustify,
            width: '100%',
          }}
        >
          {metaAlign === 'end' ? (
            <>
              {monthYearBlock}
              {circleEl}
            </>
          ) : (
            <>
              {circleEl}
              {monthYearBlock}
            </>
          )}
        </Box>
        <Divider sx={{ borderColor: alpha(resolvedAccent, 0.35) }} />
        <Typography
          sx={{
            fontFamily: '"Roboto Mono", monospace',
            fontWeight: 700,
            fontSize: '0.9rem',
            textAlign: 'center',
            color: resolvedAccent,
            opacity: 0.85,
          }}
        >
          {timeOnly}
        </Typography>
      </Box>
    </Box>
  );
};

/** Parse API start time (ISO string, ms number, Date, or Mongo `{ $date }`) to epoch ms. */
function parseCampaignStartTimestampMs(value: unknown): number {
  if (value == null || value === '') return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? NaN : t;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'object' && value !== null && '$date' in value) {
    const raw = (value as { $date: string | number | Date }).$date;
    if (raw instanceof Date) return raw.getTime();
    if (typeof raw === 'number') return new Date(raw).getTime();
    if (typeof raw === 'string') return new Date(raw).getTime();
  }
  try {
    const t = new Date(value as string | number).getTime();
    return Number.isNaN(t) ? NaN : t;
  } catch {
    return NaN;
  }
}

type SvgIconComponent = typeof CampaignIcon;

/** Timeline circle icon by template/campaign type (matches `getCampaignTypeBadge` normalization). */
function getCampaignTemplateTimelineIcon(campaignType: string | undefined): SvgIconComponent {
  const ct = (campaignType || '').toLowerCase();
  switch (ct) {
    case 'whatsapp':
    case 'whatsappbusiness':
      return WhatsAppIcon;
    case 'telegram':
      return TelegramIcon;
    case 'mail':
    case 'email':
      return EmailIcon;
    case 'sms':
      return SmsIcon;
    default:
      return CampaignIcon;
  }
}

/**
 * Single shared 1s clock via useSyncExternalStore so countdowns stay live under React 18 concurrent updates.
 */
let secondClockMs = Date.now();
const secondClockListeners = new Set<() => void>();
let secondClockIntervalId: number | undefined;

function subscribeSecondClock(onStoreChange: () => void) {
  secondClockMs = Date.now();
  secondClockListeners.add(onStoreChange);
  if (secondClockIntervalId == null) {
    secondClockIntervalId = window.setInterval(() => {
      secondClockMs = Date.now();
      secondClockListeners.forEach((l) => l());
    }, 1000);
  }
  return () => {
    secondClockListeners.delete(onStoreChange);
    if (secondClockListeners.size === 0 && secondClockIntervalId != null) {
      window.clearInterval(secondClockIntervalId);
      secondClockIntervalId = undefined;
    }
  };
}

function getSecondClockSnapshot() {
  return secondClockMs;
}

function useSecondClock(): number {
  return useSyncExternalStore(subscribeSecondClock, getSecondClockSnapshot, getSecondClockSnapshot);
}

/**
 * Live timer strip placed under the campaign panel (desktop): elapsed or remaining time, no label.
 */
const TimelineCampaignTimerStrip: React.FC<{
  startTimestamp: unknown;
  tabId: string;
  /** 'left' = campaign is on the left side → align strip to flex-end (toward center), 'right' = flex-start */
  side?: 'left' | 'right';
}> = ({
  startTimestamp,
  tabId,
  side = 'right',
}) => {
  const theme = useTheme();
  const nowTick = useSecondClock();
  const isDark = theme.palette.mode === 'dark';
  const isScheduledTab = tabId === 'scheduled';
  const startMs = parseCampaignStartTimestampMs(startTimestamp);
  const diffMs = Number.isNaN(startMs)
    ? NaN
    : isScheduledTab
      ? Math.max(0, startMs - nowTick)
      : Math.max(0, nowTick - startMs);

  const timerLabel = Number.isNaN(diffMs)
    ? '--:--:--'
    : isScheduledTab && diffMs === 0
      ? 'STARTING...'
      : (() => {
          const dd = Math.floor(diffMs / 86400000);
          const hh = Math.floor((diffMs % 86400000) / 3600000);
          const mm = Math.floor((diffMs % 3600000) / 60000);
          const ss = Math.floor((diffMs % 60000) / 1000);
          if (dd > 0) return `${dd}d ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
          return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
        })();

  const colors = isDark
    ? { cdBg: '#67AC5B', cdFg: '#ffffff' }
    : {
        cdBg: alpha(theme.palette.success.main, 0.22),
        cdFg: theme.palette.success.dark,
      };

  return (
    <Box
      sx={{
        mt: 1,
        bgcolor: colors.cdBg,
        color: colors.cdFg,
        borderRadius: 2,
        px: 2,
        py: 1,
        width: 'fit-content',
        textAlign: 'center',
        boxShadow: isDark ? 2 : 'none',
        border: isDark ? 'none' : `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
        alignSelf: side === 'left' ? 'flex-end' : 'flex-start',
      }}
    >
      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        {timerLabel}
      </Typography>
    </Box>
  );
};

/**
 * Timeline opposite column: countdown card (green) → calendar card → start time strip.
 * With campaignPanelBg, the calendar matches the campaign panel surface and uses secondary border accents.
 */
const TimelineOppositeMetaStack: React.FC<TimelineOppositeMetaStackProps> = ({
  startTimestamp,
  tabId,
  campaignPanelBg,
  variant = 'default',
}) => {
  const theme = useTheme();
  const nowTick = useSecondClock();

  const isDark = theme.palette.mode === 'dark';
  const isScheduledTab = tabId === 'scheduled';

  const startMsParsed = parseCampaignStartTimestampMs(startTimestamp);
  const d = new Date(startMsParsed);
  const dayNum = d.getDate();
  const monthShort = d.toLocaleDateString('en-US', { month: 'short' }).replace('.', '').toUpperCase();
  const year = d.getFullYear();
  const timeOnly = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const colors = isDark
    ? {
        cdBg: '#67AC5B',
        cdFg: '#ffffff',
        calBg: '#212B5E',
        calFg: '#F5E6AD',
        calCircleBorder: '#F5E6AD',
        timeBg: '#10182D',
        timeFg: '#F5E6AD',
      }
    : {
        cdBg: alpha(theme.palette.success.main, 0.22),
        cdFg: theme.palette.success.dark,
        calBg: theme.palette.grey[100],
        calFg: theme.palette.primary.dark,
        calCircleBorder: theme.palette.primary.main,
        timeBg: theme.palette.grey[300],
        timeFg: theme.palette.text.primary,
      };

  const useCampaignSurface = Boolean(campaignPanelBg);
  const calBgResolved = campaignPanelBg ?? colors.calBg;
  const calCircleBorderResolved = useCampaignSurface ? theme.palette.secondary.main : colors.calCircleBorder;

  const diffMs = Number.isNaN(startMsParsed)
    ? NaN
    : isScheduledTab
      ? Math.max(0, startMsParsed - nowTick)
      : Math.max(0, nowTick - startMsParsed);

  const mobileTimerLabel = Number.isNaN(diffMs)
    ? '--:--:--'
    : isScheduledTab && diffMs === 0
      ? 'STARTING...'
      : (() => {
          const dd = Math.floor(diffMs / 86400000);
          const hh = Math.floor((diffMs % 86400000) / 3600000);
          const mm = Math.floor((diffMs % 3600000) / 60000);
          const ss = Math.floor((diffMs % 60000) / 1000);
          if (dd > 0) return `${dd}d ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
          return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
        })();

  const countdownBox = (
    <Box
      sx={{
        bgcolor: colors.cdBg,
        color: colors.cdFg,
        borderRadius: variant === 'unifiedMobile' ? 0 : 2,
        px: 2,
        py: 1.25,
        width: variant === 'unifiedMobile' ? '100%' : '92%',
        textAlign: 'center',
        boxShadow: variant === 'unifiedMobile' ? 0 : isDark ? 2 : 'none',
        border: isDark ? 'none' : `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
      }}
    >
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.95 }}>
        {isScheduledTab ? 'Count Down' : 'Run Time'}
      </Typography>
      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 800, fontSize: '1.05rem', mt: 0.35, lineHeight: 1.2 }}>
        {mobileTimerLabel}
      </Typography>
    </Box>
  );

  const dateRowInner = (
    <>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: `3px solid ${calCircleBorderResolved}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: useCampaignSurface ? 'text.primary' : undefined,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1 }}>{dayNum}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.15rem',
            letterSpacing: 2,
            lineHeight: 1.2,
            color: useCampaignSurface ? 'text.primary' : undefined,
          }}
        >
          {monthShort}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            mt: 0.25,
            color: useCampaignSurface ? 'text.secondary' : undefined,
            opacity: useCampaignSurface ? 1 : 0.95,
          }}
        >
          {year}
        </Typography>
      </Box>
    </>
  );

  const calendarBox = (
    <Box
      sx={{
        bgcolor: calBgResolved,
        color: useCampaignSurface ? 'text.primary' : colors.calFg,
        borderRadius: variant === 'unifiedMobile' ? 1 : 2,
        p: 1.5,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        boxShadow: useCampaignSurface ? 1 : isDark ? 4 : 1,
        border: '1px solid',
        borderColor: 'secondary.main',
      }}
    >
      {dateRowInner}
    </Box>
  );

  const timeBox = (
    <Box
      sx={{
        bgcolor: colors.timeBg,
        color: colors.timeFg,
        borderRadius: variant === 'unifiedMobile' ? 0 : 2,
        px: 2,
        py: 0.85,
        width: variant === 'unifiedMobile' ? '100%' : '78%',
        textAlign: 'center',
        boxShadow: variant === 'unifiedMobile' ? 0 : isDark ? 2 : 'none',
        border: variant === 'unifiedMobile' ? 'none' : isDark ? 'none' : `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 700, fontSize: '0.9rem' }}>{timeOnly}</Typography>
    </Box>
  );

  if (variant === 'unifiedMobile') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: '100%',
          maxWidth: 'none',
        }}
      >
        {countdownBox}
        <Divider />
        <Box sx={{ px: 1.25, py: 1 }}>{calendarBox}</Box>
        <Divider />
        {timeBox}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        maxWidth: 280,
      }}
    >
      {countdownBox}
      {calendarBox}
      {timeBox}
    </Box>
  );
};

const steps = ['Select Template', 'Select DataSet', 'Configure Conditions', 'Set Interval', 'Schedule Campaign'];

export const Runner: React.FC = () => {
  const theme = useTheme();
  const heroTileFg = theme.palette.dashboard?.heroTileForegroundColor;
  const heroTileLabelColor = theme.palette.dashboard?.heroTileLabelColor
    ?? (heroTileFg ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255,255,255,0.85)');
  const heroTileTextShadow = heroTileFg ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
  const navigate = useNavigate();
  const { hasInstantUpdateAccess, isUserSuperAdmin, canManageCampaigns, getInstantUpdateRole } = useModulePermissions();
  const user = useSelector((state: RootState) => state.authentication.user);
  const company = useSelector((state: RootState) => state.authentication.company);
  const labels = useSelector((state: RootState) => selectLabels(state));
  const t = useCallback((key: string, fallback: string) => labels?.[key] ?? fallback, [labels]);
  const stepsLabels = useMemo(() => ([
    t('modules-instantupdate-runner-steps-select-template', 'Select Template'),
    t('modules-instantupdate-runner-steps-select-dataset', 'Select DataSet'),
    t('modules-instantupdate-runner-steps-configure-conditions', 'Configure Conditions'),
    t('modules-instantupdate-runner-steps-set-interval', 'Set Interval'),
    t('modules-instantupdate-runner-steps-schedule', 'Schedule Campaign')
  ]), [t]);

  // Tab configuration for campaign status filtering
  type TabConfig = {
    id: string;
    label: string;
    labelKey: string;
    icon: React.ReactElement;
    gradient: string;
    hoverGradient: string;
    statusFilter: string[];
    showRefreshButton: boolean;
    showNewCampaignButton: boolean;
    showSortOptions: boolean;
    showTimelineView: boolean;
  };

  const RUNNER_TABS: TabConfig[] = useMemo(() => [
    {
      id: 'scheduled',
      label: t('modules-instantupdate-runner-tab-scheduled', 'Scheduled'),
      labelKey: 'modules-instantupdate-runner-tab-scheduled',
      icon: <ScheduleIcon fontSize="small" />,
      gradient: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
      hoverGradient: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)',
      statusFilter: ['scheduled'],
      showRefreshButton: true,
      showNewCampaignButton: true,
      showSortOptions: true,
      showTimelineView: true,
    },
    {
      id: 'in_progress',
      label: t('modules-instantupdate-runner-tab-in-progress', 'In Progress'),
      labelKey: 'modules-instantupdate-runner-tab-in-progress',
      icon: <HourglassEmptyIcon fontSize="small" />,
      gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      hoverGradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      statusFilter: ['queued', 'running', 'blocked', 'quota_limit_reached', 'awaiting_processing'],
      showRefreshButton: true,
      showNewCampaignButton: false,
      showSortOptions: false,
      showTimelineView: true,
    },
    {
      id: 'completed',
      label: t('modules-instantupdate-runner-tab-completed', 'Completed'),
      labelKey: 'modules-instantupdate-runner-tab-completed',
      icon: <TaskAltIcon fontSize="small" />,
      gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
      hoverGradient: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
      statusFilter: ['completed', 'failed', 'cancelled', 'stopped'],
      showRefreshButton: false,
      showNewCampaignButton: false,
      showSortOptions: true,
      showTimelineView: false,
    },
  ], [t]);

  const RUNNER_ACTIVE_TAB_STORAGE_KEY = 'instant-updates-runner-active-tab';

  // Tab state (restored from localStorage on load)
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(RUNNER_ACTIVE_TAB_STORAGE_KEY);
      if (stored === 'scheduled' || stored === 'in_progress' || stored === 'completed') {
        return stored;
      }
    } catch {
      /* ignore quota / private mode */
    }
    return 'scheduled';
  });
  const activeTabConfig = useMemo(() => 
    RUNNER_TABS.find(tab => tab.id === activeTab) || RUNNER_TABS[0],
    [activeTab, RUNNER_TABS]
  );

  // Separate refresh triggers for each tab
  const [refreshTriggers, setRefreshTriggers] = useState<Record<string, number>>({
    scheduled: 0,
    in_progress: 0,
    completed: 0,
  });

  // Per-tab view mode (scheduled & in_progress default to timeline)
  type ViewMode = 'tile' | 'table' | 'timeline';
  const VIEW_MODE_DEFAULTS: Record<string, ViewMode> = { scheduled: 'timeline', in_progress: 'timeline', completed: 'tile' };
  const RUNNER_VIEW_STORAGE_KEY = 'runner-view-modes';
  const [runnerViewModes, setRunnerViewModes] = useState<Record<string, ViewMode>>(() => {
    try {
      const stored = localStorage.getItem(RUNNER_VIEW_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed === 'object' && parsed !== null) return { ...VIEW_MODE_DEFAULTS, ...parsed };
      }
    } catch { /* ignore */ }
    return { ...VIEW_MODE_DEFAULTS };
  });
  const getViewMode = (tabId: string): ViewMode => runnerViewModes[tabId] || VIEW_MODE_DEFAULTS[tabId] || 'tile';
  const setViewMode = (tabId: string, mode: ViewMode) => {
    setRunnerViewModes(prev => {
      const next = { ...prev, [tabId]: mode };
      try { localStorage.setItem(RUNNER_VIEW_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const runnerViewMode = getViewMode(activeTab);
  const TILE_PAGE_SIZE = 20;
  const [tileData, setTileData] = useState<Record<string, any[]>>({ scheduled: [], in_progress: [], completed: [] });
  const [tileLoading, setTileLoading] = useState<Record<string, boolean>>({ scheduled: false, in_progress: false, completed: false });
  const [tileSearchQueries, setTileSearchQueries] = useState<Record<string, string>>({ scheduled: '', in_progress: '', completed: '' });
  const [tileVisibleCounts, setTileVisibleCounts] = useState<Record<string, number>>({ scheduled: TILE_PAGE_SIZE, in_progress: TILE_PAGE_SIZE, completed: TILE_PAGE_SIZE });

  // Sort state per tab
  const SORT_BY_OPTIONS = [
    { value: 'startTimestamp', label: 'Start Timestamp' },
    { value: 'createdAt', label: 'Created At' },
    { value: 'updatedAt', label: 'Last Updated' },
  ];
  const SORT_ORDER_OPTIONS = [
    { value: 'desc', label: 'DESC' },
    { value: 'asc', label: 'ASC' },
  ];
  const [tabSortBy, setTabSortBy] = useState<Record<string, string>>({ scheduled: 'startTimestamp', in_progress: 'startTimestamp', completed: 'startTimestamp' });
  const [tabSortOrder, setTabSortOrder] = useState<Record<string, string>>({ scheduled: 'desc', in_progress: 'desc', completed: 'desc' });

  const instantUpdateActivePlan = useMemo((): ProductPlan | null => {
    if (!company?.productPlans?.length) return null;
    const plan = company.productPlans.find(
      (p: ProductPlan) => p.module === 'instantupdate.ai' && p.status === 'active'
    );
    return plan ?? null;
  }, [company]);

  /** SMS in Step 1 only when instantupdate.ai plan has a non-empty GSM worker name; then only approved SMS templates. */
  const hasGsmWorkerConfiguredForSms = useMemo(() => {
    const raw = instantUpdateActivePlan?.gsmWorkerName;
    return typeof raw === 'string' && raw.trim().length > 0;
  }, [instantUpdateActivePlan]);

  const [templates, setTemplates] = useState<Template[]>([]);
  const filteredTemplates = useMemo(() => {
    return templates.filter((t: any) => {
      const type = String(t?.templateType || '').toLowerCase();
      if (type === 'sms') {
        if (!hasGsmWorkerConfiguredForSms) return false;
        return t?.isApproved === true;
      }
      if (type === 'mail' || type === 'email') {
        return t?.isApproved === true;
      }
      if (type === 'whatsapp') {
        return true;
      }
      return true;
    });
  }, [templates, hasGsmWorkerConfiguredForSms]);
  const [dataSets, setDataSets] = useState<DataSets[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [wizardData, setWizardData] = useState<CreateCampaignRunnerRequest>({
    templateId: '',
    dataSetId: '',
    startTimestamp: getCurrentLocalDateTime(),
    timeInterval: 60,
    campaignName: '',
    description: '',
    filters: {
      requireConsent: 'all',
      conditions: [],
      conditionLogic: 'AND'
    }
  });
  const [wizardErrors, setWizardErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!wizardData.templateId) return;
    if (!filteredTemplates.some((x) => x._id === wizardData.templateId)) {
      setWizardData((prev) => ({ ...prev, templateId: '' }));
    }
  }, [filteredTemplates, wizardData.templateId]);
  
  // Conditions state
  const [selectedDataSetSchema, setSelectedDataSetSchema] = useState<any>(null);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);

  // Confirmation dialog states
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [stopConfirmDialogOpen, setStopConfirmDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<CampaignWithDetails | null>(null);
  const [campaignToStop, setCampaignToStop] = useState<CampaignWithDetails | null>(null);

  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState<CampaignWithDetails | null>(null);

  // Mobile menu states
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Selected template helpers
  const selectedTemplate = useMemo(() => {
    return filteredTemplates.find(t => t._id === wizardData.templateId) || null;
  }, [filteredTemplates, wizardData.templateId]);
  const isWhatsAppTemplate = useMemo(() => {
    return String(selectedTemplate?.templateType || '').toLowerCase() === 'whatsapp';
  }, [selectedTemplate]);
  const isWhatsAppOrTelegramTemplate = useMemo(() => {
    const type = String(selectedTemplate?.templateType || '').toLowerCase();
    return type === 'whatsapp' || type === 'telegram';
  }, [selectedTemplate]);

  // Validation function for each step
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0: // Select Template
        return !!wizardData.templateId;
      case 1: // Select Dataset
        return !!wizardData.dataSetId;
      case 2: // Configure Conditions
        // Conditions are optional, so this step is always valid
        return true;
      case 3: // Set Interval
        return wizardData.timeInterval > 0;
      case 4: // Schedule Campaign
        return !!(wizardData.campaignName && wizardData.startTimestamp);
      default:
        return false;
    }
  };

  // Check if user has access to InstantUpdate.ai module
  if (!hasInstantUpdateAccess() && !isUserSuperAdmin) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          {t('modules-instantupdate-runner-access-denied-title', 'Access Denied')}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {t('modules-instantupdate-runner-access-denied-subtitle', "You don't have permission to access the Campaign Runner.")}
        </Typography>
      </Box>
    );
  }

  const pendingFetches = useRef<Record<string, Promise<any>>>({});
  const deduplicatedGet = useCallback((endpoint: string) => {
    let promise = pendingFetches.current[endpoint];
    if (!promise) {
      promise = apiClient.get(endpoint);
      pendingFetches.current[endpoint] = promise;
      promise.finally(() => { delete pendingFetches.current[endpoint]; });
    }
    return promise;
  }, []);

  const fetchDataSetSchema = async (dataSetId: string) => {
    try {
      const selectedDataSet = dataSets.find(d => d._id === dataSetId);
      if (!selectedDataSet) return;

      // Extract base collection name from full collection name
      // Full format: "IU-{companyId}-{baseName}"
      const fullCollectionName = selectedDataSet.collectionName;
      const parts = fullCollectionName.split('-');
      const baseCollectionName = parts.length >= 3 ? parts.slice(2).join('-') : fullCollectionName;

      Logger.debug('Runner.tsx', `Fetching schema for dataset: ${selectedDataSet._id}, full collection: ${fullCollectionName}, base collection: ${baseCollectionName}`);
      
      const data = await apiClient.get(`/iu/ds/cmp/set/entry/schema/${encodeURIComponent(baseCollectionName)}`);
      const schema = data.data;
      setSelectedDataSetSchema(schema);
      
      // Extract available columns (excluding system fields)
      const columns = Object.keys(schema.paths || {}).filter(key => 
                        !['_id', 'createdAt', 'updatedAt', '__v'].includes(key)
      );
      setAvailableColumns(columns);
      Logger.debug('Runner.tsx', `Available columns: ${columns.join(', ')}`);
    } catch (err) {
      Logger.error('Runner.tsx', `Failed to fetch dataset schema: ${err}`);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        const [tplData, dsData] = await Promise.all([
          deduplicatedGet('/iu/contenttemplate/company'),
          deduplicatedGet('/iu/ds/cmp/set'),
        ]);
        if (cancelled) return;
        setTemplates(Array.isArray(tplData.data) ? tplData.data : []);
        setDataSets(Array.isArray(dsData.data) ? dsData.data : []);
      } catch (err) {
        if (cancelled) return;
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        Logger.error('Runner.tsx', `Failed to load initial data: ${err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [deduplicatedGet]);

  // Custom data processor to enrich campaigns with template and dataset names
  const enrichCampaignData = useCallback((data: any[]) => {
    return data.map((campaign: CampaignRunner) => {
      const template = templates.find(t => t._id === campaign.templateId);
      const dataSet = dataSets.find(d => d._id === campaign.dataSetId);
      // Map API statuses to UI statuses
      let mappedStatus: string = campaign.status as string;
      if ((campaign.status as string) === 'complete') mappedStatus = 'completed';
      else if ((campaign.status as string) === 'queued') mappedStatus = 'queued';
      else if ((campaign.status as string) === 'running') mappedStatus = 'running';
      return {
        ...campaign,
        status: mappedStatus as 'scheduled' | 'running' | 'queued' | 'completed' | 'cancelled' | 'stopped' | 'blocked' | 'awaiting_processing' | 'failed' | 'quota_limit_reached',
        templateName: template?.templateName?.split("-").slice(2).join("-") || 'Unknown Template',
        dataSetName: dataSet?.collectionName?.split("-").slice(2).join("-") || 'Unknown Dataset',
        createdByUserName: user?.profile?.firstName + ' ' + user?.profile?.lastName || 'Unknown User',
      };
    });
  }, [templates, dataSets, user]);

  const fetchTileData = useCallback(async (tabId: string) => {
    const tabConfig = RUNNER_TABS.find(tc => tc.id === tabId);
    if (!tabConfig) return;
    setTileLoading(prev => ({ ...prev, [tabId]: true }));
    try {
      let url = `/iu/campaignrunner?status=${tabConfig.statusFilter.join(',')}`;
      if (tabConfig.showSortOptions) {
        url += `&sortBy=${tabSortBy[tabId] || 'startTimestamp'}&sortOrder=${tabSortOrder[tabId] || 'desc'}`;
      }
      const res = await deduplicatedGet(url);
      const raw = Array.isArray(res.data) ? res.data : [];
      const enriched = enrichCampaignData(raw);
      setTileData(prev => ({ ...prev, [tabId]: enriched }));
    } catch {
      setTileData(prev => ({ ...prev, [tabId]: [] }));
    } finally {
      setTileLoading(prev => ({ ...prev, [tabId]: false }));
    }
  }, [RUNNER_TABS, enrichCampaignData, deduplicatedGet, tabSortBy, tabSortOrder]);

  const fetchTileDataRef = useRef(fetchTileData);
  fetchTileDataRef.current = fetchTileData;

  const activeTabRefreshTrigger = refreshTriggers[activeTab];
  const activeTabViewMode = getViewMode(activeTab);
  useEffect(() => {
    if ((activeTabViewMode === 'tile' || activeTabViewMode === 'timeline') && !loading) {
      fetchTileDataRef.current(activeTab);
    }
  }, [activeTabViewMode, activeTab, activeTabRefreshTrigger, loading]);

  const getFilteredTileData = useCallback((tabId: string) => {
    const data = tileData[tabId] || [];
    const q = (tileSearchQueries[tabId] || '').toLowerCase().trim();
    let result = q
      ? data.filter((c: any) =>
          [c.campaignName, c.campaignType, c.status, c.templateName, c.dataSetName]
            .filter(Boolean)
            .some((v: string) => String(v).toLowerCase().includes(q))
        )
      : [...data];

    const sortField = tabSortBy[tabId] || 'startTimestamp';
    const sortDir = tabSortOrder[tabId] || 'desc';
    result.sort((a: any, b: any) => {
      const aVal = new Date(a[sortField] || 0).getTime();
      const bVal = new Date(b[sortField] || 0).getTime();
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [tileData, tileSearchQueries, tabSortBy, tabSortOrder]);

  const getCompletionPercentage = (campaign: any): number => {
    const logTotal = campaign.executionLogTotalCount;
    const logNonPending = campaign.executionLogNonPendingCount;
    if (typeof logTotal === 'number' && logTotal > 0 && typeof logNonPending === 'number') {
      return Math.min(100, Math.round((logNonPending / logTotal) * 100));
    }
    const total = campaign.totalContacts || 0;
    if (total === 0) return 0;
    const processed = campaign.processedContacts || 0;
    return Math.min(100, Math.round((processed / total) * 100));
  };

  /** Scheduled timeline: secondary main for circle outline, connectors, and panels. */
  const getScheduledTimelineAccent = () => ({
    borderColor: theme.palette.secondary.main,
    connectorBg: theme.palette.secondary.main,
    showRunningGlow: false,
  });

  /** Border / connector colors for In Progress timeline (and tiles): status-driven accents */
  const getInProgressTimelineAccent = (status: string) => {
    switch (status) {
      case 'blocked':
        return {
          borderColor: theme.palette.error.main,
          connectorBg: theme.palette.error.main,
          showRunningGlow: false,
        };
      case 'awaiting_processing':
      case 'quota_limit_reached':
        return {
          borderColor: theme.palette.warning.main,
          connectorBg: theme.palette.warning.main,
          showRunningGlow: false,
        };
      case 'running':
        return {
          borderColor: theme.palette.secondary.main,
          connectorBg: theme.palette.secondary.main,
          showRunningGlow: true,
        };
      case 'queued':
        return {
          borderColor: theme.palette.secondary.main,
          connectorBg: theme.palette.secondary.main,
          showRunningGlow: false,
        };
      default:
        return {
          borderColor: theme.palette.secondary.main,
          connectorBg: theme.palette.secondary.main,
          showRunningGlow: false,
        };
    }
  };

  const isDomainInstantUpdate = useMemo(() => {
    try {
      return (window?.location?.hostname || '').toLowerCase().endsWith('instantupdate.ai');
    } catch { return false; }
  }, []);

  const handleRefresh = (tabId?: string) => {
    const targetTab = tabId || activeTab;
    setRefreshTriggers(prev => ({
      ...prev,
      [targetTab]: prev[targetTab] + 1
    }));
  };

  // Handle tab change - data refresh happens automatically via dataUrl change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    try {
      localStorage.setItem(RUNNER_ACTIVE_TAB_STORAGE_KEY, newValue);
    } catch {
      /* ignore quota / private mode */
    }
    // Note: No need to call handleRefresh here - the DataTableWithStaticSchema
    // component automatically fetches data when dataUrl changes
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const getStatusChip = (status: string) => {
    const rotate = keyframes`
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    `;
    const statusConfig: Record<string, { color: 'warning'|'info'|'success'|'error'|'default'; icon: React.ReactElement; label: string }> = {
      scheduled: { color: 'warning', icon: <ScheduleIcon />, label: t('modules-instantupdate-runner-status-scheduled', 'Scheduled') },
      running: { color: 'info', icon: <PlayArrowIcon />, label: t('modules-instantupdate-runner-status-running', 'Running') },
      queued: { color: 'warning', icon: <ScheduleIcon />, label: t('modules-instantupdate-runner-status-queued', 'Queued') },
      completed: { color: 'success', icon: <CheckCircleIcon />, label: t('modules-instantupdate-runner-status-completed', 'Completed') },
      cancelled: { color: 'error', icon: <CancelIcon />, label: t('modules-instantupdate-runner-status-cancelled', 'Cancelled') },
      stopped: { color: 'error', icon: <StopIcon />, label: t('modules-instantupdate-runner-status-stopped', 'Stopped') },
      blocked: { color: 'error', icon: <StopIcon />, label: t('modules-instantupdate-runner-status-blocked', 'Blocked') },
      failed: { color: 'error', icon: <CancelIcon />, label: t('modules-instantupdate-runner-status-failed', 'Failed') },
      awaiting_processing: { color: 'info', icon: <SettingsIcon sx={{ animation: `${rotate} 2s linear infinite` }} />, label: 'Awaiting Processing' },
      quota_limit_reached: { color: 'error', icon: <PauseCircleOutlineIcon />, label: 'Paused : Not enough credits' },
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color as any}
        variant="outlined"
        size="small"
      />
    );
  };

  const getCampaignTypeBadge = useCallback((campaignType: string) => {
    const ct = (campaignType || '').toLowerCase();
    let bg = 'linear-gradient(135deg, #757575 0%, #616161 100%)';
    let text = (ct || 'UNKNOWN').toUpperCase();
    switch (ct) {
      case 'whatsapp': case 'whatsappbusiness':
        bg = 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'; text = 'WHATSAPP'; break;
      case 'telegram':
        bg = 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'; text = 'TELEGRAM'; break;
      case 'mail': case 'email':
        bg = 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)'; text = 'EMAIL'; break;
      case 'sms':
        bg = 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)'; text = 'SMS'; break;
    }
    return (
      <Chip label={text} size="small" sx={{ background: bg, color: '#fff', fontWeight: 700, fontSize: '0.68rem', height: 22 }} />
    );
  }, []);

  const buildCampaignActions = useCallback((row: CampaignWithDetails) => {
    const actions: { label: string; onClick: () => void; color: 'secondary' | 'error' | 'warning'; icon: React.ReactElement }[] = [];
    const userRole = getInstantUpdateRole();
    actions.push({
      label: t('modules-instantupdate-runner-action-view-report', 'View Report'),
      onClick: () => navigate(`/instant-updates/runner/report/${row._id}`),
      color: 'secondary', icon: <AssessmentIcon />
    });
    const shouldShowDeleteForUser = (status: string): boolean => {
      if (userRole === 'admin' || isUserSuperAdmin) return true;
      if (userRole === 'user') return status === 'scheduled' || status === 'queued';
      return false;
    };
    switch (row.status as any) {
      case 'scheduled':
        if (canManageCampaigns()) {
          actions.push({ label: t('modules-instantupdate-runner-action-edit', 'Edit Campaign'), onClick: () => handleEditCampaign(row), color: 'secondary', icon: <EditIcon /> });
          if (shouldShowDeleteForUser(row.status)) actions.push({ label: t('modules-instantupdate-runner-action-delete', 'Delete Campaign'), onClick: () => handleDeleteCampaign(row), color: 'error', icon: <DeleteIcon /> });
        }
        break;
      case 'quota_limit_reached':
        if (canManageCampaigns()) actions.push({ label: t('modules-instantupdate-runner-action-stop', 'Stop Campaign'), onClick: () => handleStopCampaign(row), color: 'error', icon: <StopIcon /> });
        break;
      case 'running': case 'awaiting_processing':
        if (canManageCampaigns()) actions.push({ label: t('modules-instantupdate-runner-action-stop', 'Stop Campaign'), onClick: () => handleStopCampaign(row), color: 'warning', icon: <StopIcon /> });
        break;
      case 'queued':
        if (canManageCampaigns()) {
          actions.push({ label: t('modules-instantupdate-runner-action-stop', 'Stop Campaign'), onClick: () => handleStopCampaign(row), color: 'warning', icon: <StopIcon /> });
          if (shouldShowDeleteForUser(row.status)) actions.push({ label: t('modules-instantupdate-runner-action-delete', 'Delete Campaign'), onClick: () => handleDeleteCampaign(row), color: 'error', icon: <DeleteIcon /> });
        }
        break;
      case 'stopped': case 'cancelled': case 'completed': case 'failed':
        if (canManageCampaigns() && shouldShowDeleteForUser(row.status)) actions.push({ label: t('modules-instantupdate-runner-action-delete', 'Delete Campaign'), onClick: () => handleDeleteCampaign(row), color: 'error', icon: <DeleteIcon /> });
        break;
      case 'blocked':
        if (canManageCampaigns()) actions.push({ label: t('modules-instantupdate-runner-action-delete', 'Delete Campaign'), onClick: () => handleDeleteCampaign(row), color: 'error', icon: <DeleteIcon /> });
        break;
    }
    return actions;
  }, [getInstantUpdateRole, isUserSuperAdmin, canManageCampaigns, navigate, t]);

  const handleCreateCampaign = async () => {
    // Validate wizard data
    const errors: Record<string, string> = {};
    if (!wizardData.templateId) errors.templateId = 'Template is required';
    if (!wizardData.dataSetId) errors.dataSetId = 'Dataset is required';
    if (!wizardData.startTimestamp) errors.startTimestamp = 'Start timestamp is required';
    if (!wizardData.campaignName) errors.campaignName = 'Campaign name is required';
    if (wizardData.timeInterval <= 0) errors.timeInterval = 'Time interval must be positive';

    if (Object.keys(errors).length > 0) {
      setWizardErrors(errors);
      return;
    }

    setLoading(true);
    try {
      // Convert local datetime to UTC before sending to server
      const campaignData = {
        ...wizardData,
        startTimestamp: localDateTimeToUtc(wizardData.startTimestamp)
      };

      const url = isEditing && currentCampaign 
        ? `${CAMPAIGN_RUNNER_INDIVIDUAL_URL}/${currentCampaign._id}`
        : CAMPAIGN_RUNNER_URL;
      
      const method = isEditing ? 'PUT' : 'POST';

      // Use ApiClient instead of fetch (automatically includes Authorization header)
      let data;
      if (isEditing && currentCampaign) {
        data = await apiClient.put(`/iu/campaignrunner/${currentCampaign._id}`, campaignData);
      } else {
        data = await apiClient.post('/iu/campaignrunner', campaignData);
      }

      trackAction(
        isEditing ? 'campaign_updated' : 'campaign_scheduled',
        undefined,
        {
          campaign_id: (data as any)?.data?._id || currentCampaign?._id,
          campaign_name: campaignData.campaignName,
          template_id: campaignData.templateId,
          dataset_id: campaignData.dataSetId,
          start_timestamp: campaignData.startTimestamp,
          time_interval: campaignData.timeInterval,
          user_email: user?.email,
        },
      );

      setSnackbar({ 
        open: true, 
        message: `Campaign ${isEditing ? 'updated' : 'created'} successfully!`, 
        severity: 'success' 
      });
      
      // Reset wizard state
      setWizardOpen(false);
      setActiveStep(0);
      setIsEditing(false);
      setCurrentCampaign(null);
      setWizardData({
        templateId: '',
        dataSetId: '',
        startTimestamp: getCurrentLocalDateTime(),
        timeInterval: 60,
        campaignName: '',
        description: '',
        filters: {
          requireConsent: 'all',
          conditions: [],
          conditionLogic: 'AND'
        }
      });
      setWizardErrors({});
      setSelectedDataSetSchema(null);
      setAvailableColumns([]);
      
      // Trigger refresh of the data table
      handleRefresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      Logger.error('Runner.tsx', `Failed to ${isEditing ? 'update' : 'create'} campaign: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleWizardNext = () => {
    setActiveStep((prevStep) => {
      // Skip Set Interval (index 3) when not WhatsApp
      if (!isWhatsAppTemplate && prevStep === 2) {
        return prevStep + 2;
      }
      return prevStep + 1;
    });
  };

  const handleWizardBack = () => {
    setActiveStep((prevStep) => {
      // Skip back over Set Interval when not WhatsApp
      if (!isWhatsAppTemplate && prevStep === 4) {
        return prevStep - 2;
      }
      return prevStep - 1;
    });
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setActiveStep(0);
    setIsEditing(false);
    setCurrentCampaign(null);
    setWizardData({
      templateId: '',
      dataSetId: '',
      startTimestamp: getCurrentLocalDateTime(),
      timeInterval: 60,
      campaignName: '',
      description: '',
      filters: {
        requireConsent: 'all',
        conditions: [],
        conditionLogic: 'AND'
      }
    });
    setWizardErrors({});
    setSelectedDataSetSchema(null);
    setAvailableColumns([]);
  };

  // Auto-jump if interval step is active but template is not WhatsApp
  useEffect(() => {
    if (!isWhatsAppTemplate && activeStep === 3) {
      setActiveStep(4);
    }
  }, [isWhatsAppTemplate, activeStep]);

  const handleEditCampaign = (campaign: CampaignWithDetails) => {
    // Pre-populate the wizard data with existing campaign data
    setWizardData({
      templateId: campaign.templateId,
      dataSetId: campaign.dataSetId,
      startTimestamp: utcToLocalDateTime(campaign.startTimestamp),
      timeInterval: campaign.timeInterval,
      campaignName: campaign.campaignName,
      description: campaign.description || '',
      filters: campaign.filters || {
        requireConsent: 'all',
        conditions: [],
        conditionLogic: 'AND'
      }
    });

    // Clear any existing errors
    setWizardErrors({
      templateId: '',
      dataSetId: '',
      campaignName: '',
      startTimestamp: '',
      timeInterval: ''
    });

    // Set editing mode and current campaign
    setIsEditing(true);
    setCurrentCampaign(campaign);
    
    // Fetch schema for the selected dataset
    fetchDataSetSchema(campaign.dataSetId);

    // Open the wizard
    setWizardOpen(true);
    setActiveStep(0);
  };

  const handleDeleteCampaign = (campaign: CampaignWithDetails) => {
    setCampaignToDelete(campaign);
    setDeleteConfirmDialogOpen(true);
  };

  const handleStopCampaign = (campaign: CampaignWithDetails) => {
    setCampaignToStop(campaign);
    setStopConfirmDialogOpen(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    setLoading(true);
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      await apiClient.delete(`/iu/campaignrunner/${campaignToDelete._id}`);

      setSnackbar({ open: true, message: 'Campaign deleted successfully!', severity: 'success' });
      handleRefresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      Logger.error('Runner.tsx', `Failed to delete campaign: ${err}`);
    } finally {
      setLoading(false);
      setDeleteConfirmDialogOpen(false);
      setCampaignToDelete(null);
    }
  };

  const confirmStopCampaign = async () => {
    if (!campaignToStop) return;

    setLoading(true);
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      await apiClient.patch(`/iu/campaignrunner/${campaignToStop._id}/stop`);

      setSnackbar({ open: true, message: 'Campaign stopped successfully!', severity: 'success' });
      handleRefresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      Logger.error('Runner.tsx', `Failed to stop campaign: ${err}`);
    } finally {
      setLoading(false);
      setStopConfirmDialogOpen(false);
      setCampaignToStop(null);
    }
  };

  const renderWizardStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {!isMobile && (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('modules-instantupdate-runner-select-template-title', 'Select Template')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {t('modules-instantupdate-runner-select-template-subtitle', 'Choose a template for your campaign message')}
                </Typography>
              </>
            )}
            <Autocomplete
              options={filteredTemplates}
              getOptionLabel={(option) => option.templateName?.split("-").slice(2).join("-") || 'Unknown Template'}
              value={filteredTemplates.find(t => t._id === wizardData.templateId) || null}
              onChange={(_, newValue) => {
                setWizardData(prev => ({ ...prev, templateId: newValue?._id || '' }));
                setWizardErrors(prev => ({ ...prev, templateId: '' }));
              }}
              renderOption={(props, option) => {
                const templateType = (option.templateType || '').toLowerCase();
                let typeLabel = templateType.toUpperCase();
                let typeColor = 'default';
                let typeBgColor = theme.palette.grey[300];
                
                switch (templateType) {
                  case 'whatsapp':
                    typeLabel = 'WHATSAPP';
                    typeColor = '#fff';
                    typeBgColor = '#25D366';
                    break;
                  case 'mail':
                    typeLabel = 'EMAIL';
                    typeColor = '#fff';
                    typeBgColor = '#ed6c02';
                    break;
                  case 'sms':
                    typeLabel = 'SMS';
                    typeColor = '#fff';
                    typeBgColor = '#9c27b0';
                    break;
                  case 'telegram':
                    typeLabel = 'TELEGRAM';
                    typeColor = '#fff';
                    typeBgColor = '#0088cc';
                    break;
                  default:
                    typeColor = theme.palette.text.primary;
                    typeBgColor = theme.palette.grey[300];
                }
                
                return (
                  <Box 
                    component="li" 
                    {...props}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {option.templateName?.split("-").slice(2).join("-") || 'Unknown Template'}
                    </Typography>
                    <Chip
                      label={typeLabel}
                      size="small"
                      sx={{
                        ml: 1,
                        minWidth: 80,
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: typeColor,
                        backgroundColor: typeBgColor,
                      }}
                    />
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('modules-instantupdate-runner-select-template-label', 'Template *')}
                  error={!!wizardErrors.templateId}
                  helperText={wizardErrors.templateId || t('modules-instantupdate-runner-select-template-helper', 'Select a template to continue')}
                  fullWidth
                  size={isMobile ? "medium" : "small"}
                />
              )}
            />
            {isWhatsAppOrTelegramTemplate && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'warning.main',
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  '@keyframes runnerWizardWaTgRiskGlowBlink': {
                    '0%, 100%': {
                      boxShadow: `0 0 6px ${alpha(theme.palette.warning.main, 0.5)}, 0 0 14px ${alpha(theme.palette.warning.main, 0.28)}`,
                      opacity: 1,
                    },
                    '50%': {
                      boxShadow: `0 0 18px ${alpha(theme.palette.warning.main, 0.9)}, 0 0 32px ${alpha(theme.palette.warning.main, 0.5)}`,
                      opacity: 0.88,
                    },
                  },
                  animation: 'runnerWizardWaTgRiskGlowBlink 1.75s ease-in-out infinite',
                })}
              >
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <WarningAmberIcon
                    sx={{ color: 'warning.main', flexShrink: 0, fontSize: 30, mt: 0.35 }}
                    aria-hidden
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      component="h3"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 800,
                        color: 'warning.dark',
                        letterSpacing: '0.02em',
                        mb: 1,
                        lineHeight: 1.3,
                      }}
                    >
                      {t('modules-instantupdate-runner-wa-telegram-risk-panel-title', 'Use at your own RISK')}
                    </Typography>
                    <Typography
                      color="text.primary"
                      sx={{ fontSize: '1rem', lineHeight: 1.55, whiteSpace: 'pre-line' }}
                    >
                      {t(
                        'modules-instantupdate-runner-wa-telegram-risk-warning',
                        'Campaigns of type WhatsApp / Telegram must be used on your own risk, as we only recommend using these type of campaign for your KNOWN WhatsApp / Telegram contacts - with 60+ seconds interval between messages in Step 4. Failing to do so or sending messages to UNKNOWN contacts may or will lead to suspension of your WhatsApp / Telegram Account by Meta / Telegram for 24 hours, AND we will NOT take responsibility for getting this UNBLOCKED. Please use this at your own risk with UNKNOWN Contacts.\nFor information, contact support@campaignking.ai'
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            {!isMobile && (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('modules-instantupdate-runner-select-dataset-title', 'Select Dataset')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {t('modules-instantupdate-runner-select-dataset-subtitle', 'Choose the dataset containing your target audience')}
                </Typography>
              </>
            )}
            <Autocomplete
              options={dataSets}
              getOptionLabel={(option) => option.collectionName?.split("-").slice(2).join("-") || 'Unknown Dataset'}
              value={dataSets.find(d => d._id === wizardData.dataSetId) || null}
              onChange={(_, newValue) => {
                setWizardData(prev => ({ ...prev, dataSetId: newValue?._id || '' }));
                setWizardErrors(prev => ({ ...prev, dataSetId: '' }));
                if (newValue?._id) {
                  fetchDataSetSchema(newValue._id);
                } else {
                  setSelectedDataSetSchema(null);
                  setAvailableColumns([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('modules-instantupdate-runner-select-dataset-label', 'Dataset *')}
                  error={!!wizardErrors.dataSetId}
                  helperText={wizardErrors.dataSetId || t('modules-instantupdate-runner-select-dataset-helper', 'Select a dataset to continue')}
                  fullWidth
                  size={isMobile ? "medium" : "small"}
                />
              )}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            {!isMobile && (
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                {t('modules-instantupdate-runner-configure-conditions-title', 'Configure Campaign Conditions')}
              </Typography>
            )}
            
            {/* Consent Requirement */}
            <Box sx={{ mb: isMobile ? 3 : 4 }}>
              {!isMobile && (
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                  {t('modules-instantupdate-runner-consent-requirements', 'Consent Requirements')}
                </Typography>
              )}
              <FormControl fullWidth>
                <InputLabel>{t('modules-instantupdate-runner-target-audience', 'Target Audience')}</InputLabel>
                <Select
                  value={wizardData.filters?.requireConsent || 'all'}
                  onChange={(e) => {
                    setWizardData(prev => ({
                      ...prev,
                      filters: {
                        ...prev.filters!,
                        requireConsent: e.target.value as 'all' | 'consent' | 'my_contacts' | 'my_contacts_consent'
                      }
                    }));
                  }}
                  label={t('modules-instantupdate-runner-target-audience', 'Target Audience')}
                  size={isMobile ? "medium" : "small"}
                >
                  <MenuItem value="all">{t('modules-instantupdate-runner-target-audience-all', 'Send to all contacts')}</MenuItem>
                  <MenuItem value="consent">{t('modules-instantupdate-runner-target-audience-consent', 'Send to contacts with consent')}</MenuItem>
                  <MenuItem value="my_contacts">{t('modules-instantupdate-runner-target-audience-my-contacts', 'Send to my contacts only')}</MenuItem>
                  <MenuItem value="my_contacts_consent">{t('modules-instantupdate-runner-target-audience-my-contacts-consent', 'Send to my contacts only with consent')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Filtering Conditions */}
            {!isMobile && (
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                {t('modules-instantupdate-runner-filtering-conditions-title', 'Filtering Conditions')}
              </Typography>
            )}
            
            {/* Condition Logic */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('modules-instantupdate-runner-condition-logic-label', 'Condition Logic')}</InputLabel>
              <Select
                value={wizardData.filters?.conditionLogic || 'AND'}
                onChange={(e) => {
                  setWizardData(prev => ({
                    ...prev,
                    filters: {
                      ...prev.filters!,
                      conditionLogic: e.target.value as 'AND' | 'OR'
                    }
                  }));
                }}
                label={t('modules-instantupdate-runner-condition-logic-label', 'Condition Logic')}
                size={isMobile ? "medium" : "small"}
              >
                <MenuItem value="AND">{t('modules-instantupdate-runner-condition-logic-and', 'AND (All conditions must be met)')}</MenuItem>
                <MenuItem value="OR">{t('modules-instantupdate-runner-condition-logic-or', 'OR (Any condition can be met)')}</MenuItem>
              </Select>
            </FormControl>

            {/* Conditions List */}
            <Box sx={{ mb: 2 }}>
              {wizardData.filters?.conditions?.map((condition, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 2, 
                  mb: 2, 
                  p: 2, 
                  border: '1px solid #ddd', 
                  borderRadius: 1,
                  alignItems: isMobile ? 'stretch' : 'center'
                }}>
                  <FormControl sx={{ minWidth: isMobile ? '100%' : 150 }}>
                    <InputLabel>{t('modules-instantupdate-runner-condition-column-label', 'Column')}</InputLabel>
                    <Select
                      value={condition.columnName}
                      onChange={(e) => {
                        const newConditions = [...(wizardData.filters?.conditions || [])];
                        newConditions[index] = { ...condition, columnName: e.target.value };
                        setWizardData(prev => ({
                          ...prev,
                          filters: {
                            ...prev.filters!,
                            conditions: newConditions
                          }
                        }));
                      }}
                      label={t('modules-instantupdate-runner-condition-column-label', 'Column')}
                      size={isMobile ? "medium" : "small"}
                    >
                      {availableColumns.map((column) => (
                        <MenuItem key={column} value={column}>{column}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: isMobile ? '100%' : 150 }}>
                    <InputLabel>{t('modules-instantupdate-runner-condition-operator-label', 'Operator')}</InputLabel>
                    <Select
                      value={condition.operator}
                      onChange={(e) => {
                        const newConditions = [...(wizardData.filters?.conditions || [])];
                        newConditions[index] = { ...condition, operator: e.target.value as any };
                        setWizardData(prev => ({
                          ...prev,
                          filters: {
                            ...prev.filters!,
                            conditions: newConditions
                          }
                        }));
                      }}
                      label={t('modules-instantupdate-runner-condition-operator-label', 'Operator')}
                      size={isMobile ? "medium" : "small"}
                    >
                      <MenuItem value="equals">{t('modules-instantupdate-runner-condition-operator-equals', 'Equal to')}</MenuItem>
                      <MenuItem value="not_equals">{t('modules-instantupdate-runner-condition-operator-not-equals', 'Not Equal to')}</MenuItem>
                      <MenuItem value="less_than">{t('modules-instantupdate-runner-condition-operator-less-than', 'Less than')}</MenuItem>
                      <MenuItem value="less_than_equal">{t('modules-instantupdate-runner-condition-operator-less-than-equal', 'Less than or equal to')}</MenuItem>
                      <MenuItem value="greater_than">{t('modules-instantupdate-runner-condition-operator-greater-than', 'Greater than')}</MenuItem>
                      <MenuItem value="greater_than_equal">{t('modules-instantupdate-runner-condition-operator-greater-than-equal', 'Greater than or equal to')}</MenuItem>
                      <MenuItem value="contains">{t('modules-instantupdate-runner-condition-operator-contains', 'Contains')}</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                  label={t('modules-instantupdate-runner-condition-value-label', 'Value')}
                    value={condition.value}
                    onChange={(e) => {
                      const newConditions = [...(wizardData.filters?.conditions || [])];
                      newConditions[index] = { ...condition, value: e.target.value };
                      setWizardData(prev => ({
                        ...prev,
                        filters: {
                          ...prev.filters!,
                          conditions: newConditions
                        }
                      }));
                    }}
                    size={isMobile ? "medium" : "small"}
                    sx={{ flexGrow: 1 }}
                  />

                  <IconButton
                    onClick={() => {
                      const newConditions = wizardData.filters?.conditions?.filter((_, i) => i !== index) || [];
                      setWizardData(prev => ({
                        ...prev,
                        filters: {
                          ...prev.filters!,
                          conditions: newConditions
                        }
                      }));
                    }}
                    color="error"
                    size={isMobile ? "large" : "small"}
                    sx={{ alignSelf: isMobile ? 'center' : 'auto' }}
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>

            {/* Add Condition Button */}
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => {
                const newCondition = {
                  columnName: availableColumns[0] || '',
                  operator: 'equals' as const,
                  value: ''
                };
                setWizardData(prev => ({
                  ...prev,
                  filters: {
                    ...prev.filters!,
                    conditions: [...(prev.filters?.conditions || []), newCondition]
                  }
                }));
              }}
              disabled={availableColumns.length === 0}
              sx={{ mb: 2 }}
              size={isMobile ? "large" : "medium"}
              fullWidth={isMobile}
            >
              {t('modules-instantupdate-runner-button-add-condition', 'Add Condition')}
            </Button>

            {availableColumns.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                {t('modules-instantupdate-runner-select-dataset-prompt', 'Please select a dataset first to configure conditions.')}
              </Typography>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            {!isMobile && (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('modules-instantupdate-runner-set-interval-title', 'Set Time Interval')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {t('modules-instantupdate-runner-set-interval-subtitle', 'Configure the delay between campaign messages')}
                </Typography>
              </>
            )}
            {(() => {
              const isIntervalTooLow = isWhatsAppTemplate && (wizardData.timeInterval || 0) < 60;
              return (
                <TextField
                  label={t('modules-instantupdate-runner-time-interval-label', 'Time Interval (seconds) *')}
                  type="number"
                  value={wizardData.timeInterval}
                  onChange={(e) => {
                    setWizardData(prev => ({ ...prev, timeInterval: parseInt(e.target.value) || 0 }));
                    setWizardErrors(prev => ({ ...prev, timeInterval: '' }));
                  }}
                  error={!!wizardErrors.timeInterval || isIntervalTooLow}
                  helperText={
                    wizardErrors.timeInterval || (isIntervalTooLow
                      ? t('modules-instantupdate-runner-time-interval-helper-wa-min', 'For WhatsApp, recommended minimum is 60 seconds.')
                      : t('modules-instantupdate-runner-time-interval-helper-general', 'Enter time interval between campaign messages (minimum 1 second)'))
                  }
                  fullWidth
                  size={isMobile ? "medium" : "small"}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('modules-instantupdate-runner-seconds-suffix', 'seconds')}</InputAdornment>,
                  }}
                  sx={isIntervalTooLow ? { '& input': { color: 'error.main', fontWeight: 600 } } : undefined}
                />
              );
            })()}
          </Box>
        );

      case 4:
        return (
          <Box>
            {!isMobile && (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('modules-instantupdate-runner-schedule-title', 'Schedule Campaign')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {t('modules-instantupdate-runner-schedule-subtitle', 'Set campaign details and schedule')}
                </Typography>
              </>
            )}
            <TextField
              label={t('modules-instantupdate-runner-campaign-name-label', 'Campaign Name *')}
              value={wizardData.campaignName}
              onChange={(e) => {
                setWizardData(prev => ({ ...prev, campaignName: e.target.value }));
                setWizardErrors(prev => ({ ...prev, campaignName: '' }));
              }}
              error={!!wizardErrors.campaignName}
              helperText={wizardErrors.campaignName || t('modules-instantupdate-runner-campaign-name-helper', 'Enter a name for your campaign')}
              fullWidth
              size={isMobile ? "medium" : "small"}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('modules-instantupdate-runner-description-label', 'Description (Optional)')}
              value={wizardData.description}
              onChange={(e) => setWizardData(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              size={isMobile ? "medium" : "small"}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('modules-instantupdate-runner-start-timestamp-label', 'Start Timestamp *')}
              type="datetime-local"
              value={wizardData.startTimestamp}
              onChange={(e) => {
                setWizardData(prev => ({ ...prev, startTimestamp: e.target.value }));
                setWizardErrors(prev => ({ ...prev, startTimestamp: '' }));
              }}
              error={!!wizardErrors.startTimestamp}
              helperText={wizardErrors.startTimestamp || t('modules-instantupdate-runner-start-timestamp-helper', 'Select when the campaign should start')}
              fullWidth
              size={isMobile ? "medium" : "small"}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Show empty state if no templates or datasets
  if (!loading && (templates.length === 0 || dataSets.length === 0)) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          {t('modules-instantupdate-runner-empty-title', 'No Templates or Datasets')}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {t('modules-instantupdate-runner-empty-subtitle', 'Please create at least one template and one dataset to use the Campaign Runner.')}
        </Typography>
      </Box>
    );
  }

  if (loading && templates.length === 0 && dataSets.length === 0) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Backdrop
          sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    );
  }

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%" mb={3}>
      <DynamicBreadcrumbs items={[
        { itemPath: "/dashboard", itemLabel: 'Home' },
        { itemPath: "/instant-updates/runner", itemLabel: t('modules-instantupdate-runner-breadcrumbs-runner', 'Runner') }
      ]} />
      
      {/* Tabs for campaign runner status */}
      <Box sx={{ mb: 0, px: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 0,
            p: 0,
            m: 0,
            '& .MuiTabs-scroller': { p: 0, m: 0 },
            '& .MuiTabs-flexContainer': { gap: 0, p: 0, m: 0 },
            '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
            '& .MuiTabs-indicator': { backgroundColor: 'secondary.main' }
          }}
        >
          {RUNNER_TABS.map(tab => (
            <Tab
              key={tab.id}
              value={tab.id}
              iconPosition="start"
              icon={tab.icon}
              label={tab.label}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mr: 0.5,
                px: 1.5,
                minHeight: 40,
                color: activeTab === tab.id ? 'common.white' : 'text.primary',
                background: activeTab === tab.id ? tab.gradient : 'transparent',
                '&.Mui-selected': {
                  color: 'common.white'
                },
                '&:hover': {
                  background: activeTab === tab.id ? tab.hoverGradient : 'action.hover'
                },
                '&:first-of-type': { ml: 0 }
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        {activeTabConfig.showRefreshButton && (
          <MenuItemComponent onClick={() => { handleRefresh(); handleMobileMenuClose(); }}>
            <RefreshIcon sx={{ mr: 2 }} />
            {t('modules-instantupdate-runner-button-refresh', 'Refresh')}
          </MenuItemComponent>
        )}
        {activeTabConfig.showSortOptions && (
          <Divider key="sort-divider" sx={{ my: 0.5 }} />
        )}
        {activeTabConfig.showSortOptions && (
          <Box key="sort-options" sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={tabSortBy[activeTab] || 'startTimestamp'}
                label="Sort By"
                onChange={(e) => {
                  setTabSortBy(prev => ({ ...prev, [activeTab]: e.target.value }));
                  setTileVisibleCounts(prev => ({ ...prev, [activeTab]: TILE_PAGE_SIZE }));
                  handleRefresh(activeTab);
                }}
              >
                {SORT_BY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Sort Order</InputLabel>
              <Select
                value={tabSortOrder[activeTab] || 'desc'}
                label="Sort Order"
                onChange={(e) => {
                  setTabSortOrder(prev => ({ ...prev, [activeTab]: e.target.value }));
                  setTileVisibleCounts(prev => ({ ...prev, [activeTab]: TILE_PAGE_SIZE }));
                  handleRefresh(activeTab);
                }}
              >
                {SORT_ORDER_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">View:</Typography>
          <ToggleButtonGroup
            value={getViewMode(activeTab)}
            exclusive
            onChange={(_e, newMode) => {
              if (newMode !== null) {
                setViewMode(activeTab, newMode);
                handleMobileMenuClose();
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: (t) => t.palette.toggle?.selectedBg ?? t.palette.secondary.main,
                  color: (t) => t.palette.toggle?.selectedColor ?? t.palette.secondary.contrastText,
                  '&:hover': { backgroundColor: (t) => t.palette.toggle?.selectedHoverBg ?? t.palette.secondary.dark }
                }
              }
            }}
          >
            <ToggleButton value="tile" aria-label="Tile view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <TableRowsIcon fontSize="small" />
            </ToggleButton>
            {activeTabConfig.showTimelineView && (
              <ToggleButton value="timeline" aria-label="Timeline view">
                <TimelineIcon fontSize="small" />
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>
      </Menu>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tab Content Panels */}
      {RUNNER_TABS.map((tab) => (
        activeTab === tab.id ? (
          <Box key={`panel-${tab.id}`}>
            {/* Mobile / tablet: prominent NEW CAMPAIGN above toolbar (not in burger menu) */}
          {tab.showNewCampaignButton && canManageCampaigns() && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                mt: 2,
                mb: 2,
                px: { xs: 0.5, sm: 1 },
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => setWizardOpen(true)}
                fullWidth
                sx={(theme) => ({
                  maxWidth: 480,
                  py: 1.25,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  borderWidth: 2,
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  textTransform: 'uppercase',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'secondary.dark',
                  },
                  '@keyframes runnerNewCampaignGlow': {
                    '0%, 100%': {
                      boxShadow: `0 0 6px ${alpha(theme.palette.secondary.main, 0.55)}, 0 0 14px ${alpha(theme.palette.secondary.main, 0.35)}`,
                    },
                    '50%': {
                      boxShadow: `0 0 16px ${alpha(theme.palette.secondary.main, 0.95)}, 0 0 28px ${alpha(theme.palette.secondary.main, 0.5)}`,
                    },
                  },
                  animation: 'runnerNewCampaignGlow 2s ease-in-out infinite',
                })}
              >
                {t('modules-instantupdate-runner-button-new-campaign', 'New Campaign')}
              </Button>
            </Box>
          )}

          <Box 
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                mb: 2
              }}
            >
              {/* Tab-specific Toolbar */}
              <Box 
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                  alignItems: 'center',
                  p: 1.5,
                  m: 1.5,
                  mb: 0,
                  borderRadius: 2,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                }}
              >
                {/* Left side - Search */}
                <TextField
                  size="small"
                  placeholder={isDomainInstantUpdate ? 'Search notifications...' : 'Search campaigns...'}
                  value={tileSearchQueries[tab.id] || ''}
                  onChange={(e) => {
                    setTileSearchQueries(prev => ({ ...prev, [tab.id]: e.target.value }));
                    setTileVisibleCounts(prev => ({ ...prev, [tab.id]: TILE_PAGE_SIZE }));
                  }}
                  sx={{ flexGrow: 1, maxWidth: 360 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Sort dropdowns (desktop only — on mobile they live in the burger menu) */}
                {tab.showSortOptions && !isMobile && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 150, ml: 'auto' }}>
                      <InputLabel>Sort By</InputLabel>
                      <Select
                        value={tabSortBy[tab.id] || 'startTimestamp'}
                        label="Sort By"
                        onChange={(e) => {
                          setTabSortBy(prev => ({ ...prev, [tab.id]: e.target.value }));
                          setTileVisibleCounts(prev => ({ ...prev, [tab.id]: TILE_PAGE_SIZE }));
                          handleRefresh(tab.id);
                        }}
                      >
                        {SORT_BY_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <InputLabel>Sort Order</InputLabel>
                      <Select
                        value={tabSortOrder[tab.id] || 'desc'}
                        label="Sort Order"
                        onChange={(e) => {
                          setTabSortOrder(prev => ({ ...prev, [tab.id]: e.target.value }));
                          setTileVisibleCounts(prev => ({ ...prev, [tab.id]: TILE_PAGE_SIZE }));
                          handleRefresh(tab.id);
                        }}
                      >
                        {SORT_ORDER_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {/* Right side - Buttons or Menu */}
                {isMobile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', ...(!tab.showSortOptions && { ml: 'auto' }) }}>
                    <IconButton
                      onClick={handleMobileMenuOpen}
                      size="large"
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    gap: 1,
                    alignItems: 'center',
                    ...(!tab.showSortOptions && { ml: 'auto' }),
                  }}>
                    {tab.showRefreshButton && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleRefresh()}
                        startIcon={<RefreshIcon />}
                        size="small"
                        sx={{ 
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('modules-instantupdate-runner-button-refresh', 'Refresh')}
                      </Button>
                    )}
                    {tab.showNewCampaignButton && canManageCampaigns() && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={() => setWizardOpen(true)}
                        size="small"
                        sx={{ 
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('modules-instantupdate-runner-button-new-campaign', 'New Campaign')}
                      </Button>
                    )}
                    <ToggleButtonGroup
                      value={getViewMode(tab.id)}
                      exclusive
                      onChange={(_e, newMode) => {
                        if (newMode !== null) {
                          setViewMode(tab.id, newMode);
                        }
                      }}
                      size="small"
                      sx={{
                        '& .MuiToggleButton-root': {
                          border: '1px solid',
                          borderColor: 'divider',
                          '&.Mui-selected': {
                            backgroundColor: (t) => t.palette.toggle?.selectedBg ?? (t.palette.mode === 'dark' ? '#191f45' : t.palette.secondary.main),
                            color: (t) => t.palette.toggle?.selectedColor ?? (t.palette.mode === 'dark' ? '#fff' : t.palette.secondary.contrastText),
                            '&:hover': { backgroundColor: (t) => t.palette.toggle?.selectedHoverBg ?? (t.palette.mode === 'dark' ? '#232a54' : t.palette.secondary.dark) }
                          }
                        }
                      }}
                    >
                      <ToggleButton value="tile" aria-label="Tile view">
                        <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                      </ToggleButton>
                      <ToggleButton value="table" aria-label="Table view">
                        <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                      </ToggleButton>
                      {tab.showTimelineView && (
                        <ToggleButton value="timeline" aria-label="Timeline view">
                          <Tooltip title="Timeline View"><TimelineIcon fontSize="small" /></Tooltip>
                        </ToggleButton>
                      )}
                    </ToggleButtonGroup>
                  </Box>
                )}
              </Box>

              {/* Tab Content: Table, Tile, or Timeline */}
              <Box sx={{ p: 2 }}>
                {getViewMode(tab.id) === 'table' ? (
                <DataTableWithStaticSchema
                  dataUrl={`${CAMPAIGN_RUNNER_URL}?status=${tab.statusFilter.join(',')}${tab.showSortOptions ? `&sortBy=${tabSortBy[tab.id] || 'startTimestamp'}&sortOrder=${tabSortOrder[tab.id] || 'desc'}` : ''}`}
                  schemaObject={campaignRunnerSchema}
                  columnsToExclude={['_id', 'companyId', 'templateId', 'dataSetId', 'createdBy', 'completedOn', 'totalContacts', 'processedContacts', 'failedContacts', 'successContacts', 'description', 'filters', 'executionLog', 'executionLogTotalCount', 'executionLogNonPendingCount', 'createdAt', 'updatedAt']}
                  refreshTrigger={refreshTriggers[tab.id]}
                  dataSetName={`campaign-runner-${tab.id}`}
                  enableColumnPreferences={true}
                  allowedOptionalColumns={['dataSetName', 'createdByUserName', 'templateName', 'createdOn', 'timeInterval']}
                  hideSearch
                  externalSearchQuery={tileSearchQueries[tab.id] || ''}
                  columnRenderers={{
                    startTimestamp: (row: CampaignWithDetails) => formatUtcToLocal(row.startTimestamp),
                    status: (row: CampaignWithDetails) => getStatusChip(row.status),
                    createdOn: (row: CampaignWithDetails) => new Date(row.createdOn).toLocaleDateString(),
                    templateName: (row: CampaignWithDetails) => row.templateName || t('modules-instantupdate-runner-unknown-template', 'Unknown Template'),
                    campaignType: (row: CampaignWithDetails) => {
                      const ct = (row as any).campaignType?.toLowerCase() || '';
                      let buttonProps = {
                        background: '',
                        hoverBackground: '',
                        color: 'primary' as any,
                        text: (ct || '').toUpperCase()
                      };
                      switch (ct) {
                        case 'whatsapp':
                        case 'whatsappbusiness':
                          buttonProps = { background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', hoverBackground: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)', color: 'success', text: 'WHATSAPP' }; break;
                        case 'telegram':
                          buttonProps = { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', hoverBackground: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'info', text: 'TELEGRAM' }; break;
                        case 'mail': case 'email':
                          buttonProps = { background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)', hoverBackground: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)', color: 'warning', text: 'EMAIL' }; break;
                        case 'sms':
                          buttonProps = { background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', hoverBackground: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', color: 'secondary', text: 'SMS' }; break;
                        default:
                          buttonProps = { background: 'linear-gradient(135deg, #757575 0%, #616161 100%)', hoverBackground: 'linear-gradient(135deg, #757575 0%, #616161 100%)', color: 'inherit' as any, text: (ct || 'UNKNOWN').toUpperCase() };
                      }
                      return (
                        <Button variant="contained" color={buttonProps.color} sx={{ background: buttonProps.background, color: 'common.white', padding: "2px 8px", fontSize: "11px", boxShadow: "none", textTransform: "none", minWidth: "auto", height: "24px", fontWeight: "bold", '&:hover': { background: buttonProps.hoverBackground } }}>
                          {buttonProps.text}
                        </Button>
                      );
                    },
                    dataSetName: (row: CampaignWithDetails) => row.dataSetName || t('modules-instantupdate-runner-unknown-dataset', 'Unknown Dataset'),
                    createdByUserName: (row: CampaignWithDetails) => row.createdByUserName || t('modules-instantupdate-runner-unknown-user', 'Unknown User'),
                  }}
                  customActions={(row: CampaignWithDetails) => buildCampaignActions(row)}
                  customDataProcessor={enrichCampaignData}
                />
                ) : getViewMode(tab.id) === 'timeline' && tab.showTimelineView ? (
                /* ── Timeline View ── */
                (() => {
                  if (tileLoading[tab.id]) {
                    return (
                      <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress sx={(th: any) => ({ color: th.palette.secondary.main })} />
                      </Box>
                    );
                  }
                  const filtered = getFilteredTileData(tab.id);
                  if (filtered.length === 0) {
                    const emptyPrimary =
                      tab.id === 'scheduled'
                        ? (isDomainInstantUpdate ? 'No scheduled notifications.' : 'No scheduled campaigns.')
                        : (isDomainInstantUpdate ? 'No notifications in progress.' : 'No campaigns in progress.');
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        {(tileData[tab.id] || []).length === 0 ? emptyPrimary : 'No results match your search.'}
                      </Typography>
                    );
                  }
                  const sorted = [...filtered].sort((a: any, b: any) =>
                    new Date(a.startTimestamp || 0).getTime() - new Date(b.startTimestamp || 0).getTime()
                  );
                  const CIRCLE_SIZE = { xs: 72, md: 88 };
                  const CIRCLE_PX_XS = 72;
                  const LINE_WIDTH = 3;
                  const TERM_W = 14;
                  const TERM_H = 6;
                  const toolbarBg = (th: any) => th.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)';
                  const renderTimelinePanelActions = (corner: 'left' | 'right', row: CampaignWithDetails) => {
                    const panelActions = buildCampaignActions(row);
                    if (panelActions.length === 0) return null;
                    return (
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          zIndex: 2,
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 0.5,
                          alignSelf: corner === 'left' ? 'flex-start' : 'flex-end',
                          justifyContent: corner === 'left' ? 'flex-start' : 'flex-end',
                          width: '100%',
                          mb: 0.75,
                        }}
                      >
                        {panelActions.filter((a) => a.color !== 'error').map((action, actionIdx) => (
                          <Tooltip key={`tl-a-${actionIdx}`} title={action.label}>
                            <Button
                              variant="contained"
                              color={action.color as any}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick();
                              }}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1, minWidth: 34, fontSize: '0.7rem' }}
                            >
                              {action.icon}
                            </Button>
                          </Tooltip>
                        ))}
                        {panelActions.filter((a) => a.color === 'error').map((action, actionIdx) => (
                          <Tooltip key={`tl-e-${actionIdx}`} title={action.label}>
                            <Button
                              variant="contained"
                              color={action.color as any}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick();
                              }}
                              sx={{ borderRadius: 1, textTransform: 'none', px: 1, minWidth: 34, fontSize: '0.7rem' }}
                            >
                              {action.icon}
                            </Button>
                          </Tooltip>
                        ))}
                      </Box>
                    );
                  };
                  return (
                    <Box sx={{ position: 'relative', py: 4, px: { xs: 1, md: 0 } }}>
                      {/* Vertical center line */}
                      <Box sx={{
                        position: 'absolute',
                        left: { xs: `${CIRCLE_PX_XS / 2}px`, md: '50%' },
                        transform: { md: 'translateX(-50%)' },
                        top: 0, bottom: 0,
                        width: LINE_WIDTH,
                        bgcolor: 'secondary.main',
                        zIndex: 0,
                      }} />
                      {/* Top terminator rectangle */}
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: { xs: `${CIRCLE_PX_XS / 2}px`, md: '50%' },
                        transform: { xs: `translateX(-${TERM_W / 2}px)`, md: `translateX(-${TERM_W / 2}px)` },
                        width: TERM_W,
                        height: TERM_H,
                        bgcolor: 'secondary.main',
                        borderRadius: 0.5,
                        zIndex: 1,
                      }} />
                      {/* Bottom terminator rectangle */}
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: { xs: `${CIRCLE_PX_XS / 2}px`, md: '50%' },
                        transform: { xs: `translateX(-${TERM_W / 2}px)`, md: `translateX(-${TERM_W / 2}px)` },
                        width: TERM_W,
                        height: TERM_H,
                        bgcolor: 'secondary.main',
                        borderRadius: 0.5,
                        zIndex: 1,
                      }} />

                      {sorted.map((campaign: any, idx: number) => {
                        const pct = getCompletionPercentage(campaign);
                        const isLeft = idx % 2 === 0;
                        const accent =
                          tab.id === 'scheduled'
                            ? getScheduledTimelineAccent()
                            : getInProgressTimelineAccent(campaign.status);
                        const campaignTypeLower = (campaign.campaignType || '').toLowerCase();
                        const isBlockedWhatsAppTimeline =
                          campaign.status === 'blocked' &&
                          (campaignTypeLower === 'whatsapp' || campaignTypeLower === 'whatsappbusiness');
                        const timelineIconColor = isBlockedWhatsAppTimeline
                          ? '#fff'
                          : accent.borderColor;
                        const TimelineTemplateIcon = getCampaignTemplateTimelineIcon(campaign.campaignType);
                        const renderTimelineOppositeMeta = (connectorAt: 'start' | 'end') => (
                          <>
                            <Box
                              sx={{
                                position: 'absolute',
                                ...(connectorAt === 'end' ? { right: 0 } : { left: 0 }),
                                top: '50%',
                                width: 24,
                                height: LINE_WIDTH,
                                bgcolor: accent.connectorBg,
                                transform: 'translateY(-50%)',
                                zIndex: 0,
                              }}
                            />
                            <Box
                              sx={{
                                position: 'relative',
                                maxWidth: 300,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                                ...(connectorAt === 'end'
                                  ? { mr: 3, textAlign: 'right', alignItems: 'flex-end' }
                                  : { ml: 3, textAlign: 'left', alignItems: 'flex-start' }),
                              }}
                            >
                              <TimelineOppositeDateOnlyPanel
                                startTimestamp={campaign.startTimestamp}
                                campaignPanelBg={toolbarBg}
                                metaAlign={connectorAt === 'end' ? 'end' : 'start'}
                                accentColor={accent.borderColor}
                              />
                            </Box>
                          </>
                        );
                        return (
                          <Box
                            key={campaign._id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: { xs: 8, md: 11 },
                              position: 'relative',
                            }}
                          >
                            {/* Desktop: left column — campaign panel (even) or date / time / countdown (odd) */}
                            <Box sx={{
                              flex: 1,
                              display: { xs: 'none', md: 'flex' },
                              justifyContent: 'flex-end',
                              pr: 0,
                              position: 'relative',
                            }}>
                              {isLeft ? (
                                <>
                                  <Box sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '50%',
                                    width: 24,
                                    height: LINE_WIDTH,
                                    bgcolor: accent.connectorBg,
                                    transform: 'translateY(-50%)',
                                    zIndex: 0,
                                  }} />
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      maxWidth: 380,
                                      mr: 3,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'stretch',
                                    }}
                                  >
                                    {renderTimelinePanelActions('left', campaign as CampaignWithDetails)}
                                    <Box
                                    onClick={() => navigate(`/instant-updates/runner/report/${campaign._id}`)}
                                    sx={{
                                      cursor: 'pointer',
                                      textAlign: 'right',
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: toolbarBg,
                                      border: '1px solid',
                                      borderColor: accent.borderColor,
                                      boxShadow: 1,
                                      transition: 'box-shadow 0.2s',
                                      '&:hover': { boxShadow: 4 },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
                                      <Typography variant="subtitle2" fontWeight={700} noWrap>{campaign.campaignName}</Typography>
                                      {tab.id === 'in_progress' && (
                                        <Chip label={`${pct}%`} size="small" sx={{ fontWeight: 700, minWidth: 48, bgcolor: accent.borderColor, color: (theme.palette.mode === 'dark' && (campaign.status === 'queued' || campaign.status === 'running')) ? '#000' : '#fff' }} />
                                      )}
                                    </Box>
                                    {tab.id === 'in_progress' && (
                                      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, mb: 1, bgcolor: alpha(accent.borderColor, 0.18), '& .MuiLinearProgress-bar': { bgcolor: accent.borderColor } }} />
                                    )}
                                    {campaign.description && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {campaign.description.length > 100 ? campaign.description.slice(0, 100) + '...' : campaign.description}
                                      </Typography>
                                    )}
                                    </Box>
                                    <TimelineCampaignTimerStrip
                                      startTimestamp={campaign.startTimestamp}
                                      tabId={tab.id}
                                      side="left"
                                    />
                                  </Box>
                                </>
                              ) : (
                                renderTimelineOppositeMeta('end')
                              )}
                            </Box>

                            {/* Center circle */}
                            <Box sx={{
                              position: 'relative',
                              zIndex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              flexShrink: 0,
                            }}>
                              {(() => {
                                const circleSx = {
                                  width: CIRCLE_SIZE,
                                  height: CIRCLE_SIZE,
                                  borderRadius: '50%',
                                  border: `${LINE_WIDTH}px solid`,
                                  borderColor: accent.borderColor,
                                  background: isBlockedWhatsAppTimeline
                                    ? darken(theme.palette.error.main, 0.22)
                                    : theme.palette.background.paper,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.25,
                                  boxShadow: isBlockedWhatsAppTimeline ? 'none' : 2,
                                  ...(isBlockedWhatsAppTimeline && {
                                    '@keyframes timelineBlockedWaGlowBlink': {
                                      '0%, 100%': {
                                        boxShadow: `0 0 8px ${alpha(theme.palette.error.main, 0.65)}, 0 0 18px ${alpha(theme.palette.error.main, 0.38)}`,
                                      },
                                      '50%': {
                                        boxShadow: `0 0 22px ${alpha(theme.palette.error.main, 0.98)}, 0 0 40px ${alpha(theme.palette.error.main, 0.58)}`,
                                      },
                                    },
                                    animation: 'timelineBlockedWaGlowBlink 1.75s ease-in-out infinite',
                                  }),
                                  cursor: 'pointer',
                                  transition:
                                    'transform 0.22s ease, filter 0.22s ease, box-shadow 0.22s ease',
                                  '&:hover': {
                                    transform: 'scale(1.07)',
                                    filter: 'brightness(1.12)',
                                    zIndex: 2,
                                    boxShadow: `0 12px 36px ${alpha(accent.borderColor, 0.5)}, 0 6px 18px ${alpha(theme.palette.common.black, 0.14)}`,
                                  },
                                  '&:active': {
                                    transform: 'scale(1.03)',
                                    filter: 'brightness(1.05)',
                                  },
                                  '&:focus-visible': {
                                    outline: `2px solid ${theme.palette.secondary.main}`,
                                    outlineOffset: 2,
                                  },
                                  ...(accent.showRunningGlow && !isBlockedWhatsAppTimeline && {
                                    '@keyframes timelineCircleGlow': {
                                      '0%, 100%': {
                                        boxShadow: `0 0 8px ${alpha(theme.palette.secondary.main, 0.6)}, 0 0 16px ${alpha(theme.palette.secondary.main, 0.35)}`,
                                      },
                                      '50%': {
                                        boxShadow: `0 0 20px ${alpha(theme.palette.secondary.main, 1)}, 0 0 36px ${alpha(theme.palette.secondary.main, 0.55)}`,
                                      },
                                    },
                                    animation: 'timelineCircleGlow 2s ease-in-out infinite',
                                  }),
                                };
                                const reportPath = `/instant-updates/runner/report/${campaign._id}`;
                                const circleBody = (
                                  <Box
                                    onClick={() => navigate(reportPath)}
                                    role="link"
                                    tabIndex={0}
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigate(reportPath);
                                      }
                                    }}
                                    aria-label={
                                      campaign.campaignName
                                        ? `${t('modules-instantupdate-runner-action-view-report', 'View Report')}: ${campaign.campaignName}`
                                        : t('modules-instantupdate-runner-action-view-report', 'View Report')
                                    }
                                    sx={circleSx}
                                  >
                                    <TimelineTemplateIcon
                                      sx={{
                                        fontSize: { xs: 26, md: 30 },
                                        color: isBlockedWhatsAppTimeline ? '#fff' : timelineIconColor,
                                      }}
                                    />
                                  </Box>
                                );
                                return circleBody;
                              })()}
                            </Box>

                            {/* Desktop: right column — campaign panel (odd) or date / time / countdown (even) */}
                            <Box sx={{
                              flex: 1,
                              display: { xs: 'none', md: 'flex' },
                              justifyContent: 'flex-start',
                              pl: 0,
                              position: 'relative',
                            }}>
                              {!isLeft ? (
                                <>
                                  <Box sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: '50%',
                                    width: 24,
                                    height: LINE_WIDTH,
                                    bgcolor: accent.connectorBg,
                                    transform: 'translateY(-50%)',
                                    zIndex: 0,
                                  }} />
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      maxWidth: 380,
                                      ml: 3,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'stretch',
                                    }}
                                  >
                                    {renderTimelinePanelActions('right', campaign as CampaignWithDetails)}
                                    <Box
                                    onClick={() => navigate(`/instant-updates/runner/report/${campaign._id}`)}
                                    sx={{
                                      cursor: 'pointer',
                                      textAlign: 'left',
                                      p: 2,
                                      borderRadius: 2,
                                      bgcolor: toolbarBg,
                                      border: '1px solid',
                                      borderColor: accent.borderColor,
                                      boxShadow: 1,
                                      transition: 'box-shadow 0.2s',
                                      '&:hover': { boxShadow: 4 },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      {tab.id === 'in_progress' && (
                                        <Chip label={`${pct}%`} size="small" sx={{ fontWeight: 700, minWidth: 48, bgcolor: accent.borderColor, color: (theme.palette.mode === 'dark' && (campaign.status === 'queued' || campaign.status === 'running')) ? '#000' : '#fff' }} />
                                      )}
                                      <Typography variant="subtitle2" fontWeight={700} noWrap>{campaign.campaignName}</Typography>
                                    </Box>
                                    {tab.id === 'in_progress' && (
                                      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, mb: 1, bgcolor: alpha(accent.borderColor, 0.18), '& .MuiLinearProgress-bar': { bgcolor: accent.borderColor } }} />
                                    )}
                                    {campaign.description && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {campaign.description.length > 100 ? campaign.description.slice(0, 100) + '...' : campaign.description}
                                      </Typography>
                                    )}
                                    </Box>
                                    <TimelineCampaignTimerStrip
                                      startTimestamp={campaign.startTimestamp}
                                      tabId={tab.id}
                                      side="right"
                                    />
                                  </Box>
                                </>
                              ) : (
                                renderTimelineOppositeMeta('start')
                              )}
                            </Box>

                            {/* Mobile: always show content on the right of the circle */}
                            <Box sx={{
                              display: { xs: 'flex', md: 'none' },
                              flex: 1,
                              pl: 0,
                              position: 'relative',
                              alignItems: 'stretch',
                            }}>
                              <Box sx={{
                                width: 16,
                                height: LINE_WIDTH,
                                bgcolor: accent.connectorBg,
                                flexShrink: 0,
                                alignSelf: 'center',
                              }} />
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'stretch',
                                }}
                              >
                                <Box
                                  sx={{
                                    borderRadius: 2,
                                    bgcolor: toolbarBg,
                                    border: '1px solid',
                                    borderColor: accent.borderColor,
                                    boxShadow: 1,
                                    width: '100%',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box onClick={(e) => e.stopPropagation()} sx={{ p: 1, pb: 0 }}>
                                    {renderTimelinePanelActions('right', campaign as CampaignWithDetails)}
                                  </Box>
                                  <TimelineOppositeMetaStack
                                    startTimestamp={campaign.startTimestamp}
                                    tabId={tab.id}
                                    campaignPanelBg={toolbarBg}
                                    variant="unifiedMobile"
                                  />
                                  <Box
                                    onClick={() => navigate(`/instant-updates/runner/report/${campaign._id}`)}
                                    sx={{
                                      cursor: 'pointer',
                                      p: 1.5,
                                      pt: 1,
                                      transition: 'box-shadow 0.2s',
                                      '&:hover': { bgcolor: (th: any) => (th.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)') },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, mb: 0.5, minWidth: 0, width: '100%' }}>
                                      {tab.id === 'in_progress' && (
                                        <Chip label={`${pct}%`} size="small" sx={{ fontWeight: 700, minWidth: 42, fontSize: '0.7rem', flexShrink: 0, bgcolor: accent.borderColor, color: (theme.palette.mode === 'dark' && (campaign.status === 'queued' || campaign.status === 'running')) ? '#000' : '#fff' }} />
                                      )}
                                      <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem', minWidth: 0, flex: '1 1 140px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{campaign.campaignName}</Typography>
                                    </Box>
                                    {tab.id === 'in_progress' && (
                                      <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 3, mb: 0.5, bgcolor: alpha(accent.borderColor, 0.18), '& .MuiLinearProgress-bar': { bgcolor: accent.borderColor } }} />
                                    )}
                                    {campaign.description && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                        {campaign.description.length > 80 ? campaign.description.slice(0, 80) + '...' : campaign.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()
                ) : (
                <>
                  {tileLoading[tab.id] ? (
                    <Box display="flex" justifyContent="center" py={6}>
                      <CircularProgress sx={(th: any) => ({ color: th.palette.secondary.main })} />
                    </Box>
                  ) : (() => {
                    const filtered = getFilteredTileData(tab.id);
                    const visibleCount = tileVisibleCounts[tab.id] || TILE_PAGE_SIZE;
                    const visible = filtered.slice(0, visibleCount);
                    return filtered.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        {(tileData[tab.id] || []).length === 0
                          ? (isDomainInstantUpdate ? 'No notifications found.' : 'No campaigns found.')
                          : 'No results match your search.'}
                      </Typography>
                    ) : (
                      <>
                        <Box display="flex" flexWrap="wrap" gap={2.5} justifyContent="normal">
                          {visible.map((campaign: any) => {
                            const actions = buildCampaignActions(campaign);
                            const ct = (campaign.campaignType || '').toLowerCase();
                            const inProgressAccent = tab.id === 'in_progress' ? getInProgressTimelineAccent(campaign.status) : null;
                            return (
                              <Box
                                key={campaign._id}
                                onClick={() => navigate(`/instant-updates/runner/report/${campaign._id}`)}
                                sx={{
                                  width: 280,
                                  height: 200,
                                  borderRadius: 2.5,
                                  overflow: 'hidden',
                                  position: 'relative',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                  border: inProgressAccent ? '2px solid' : '1px solid',
                                  borderColor: inProgressAccent ? inProgressAccent.borderColor : 'divider',
                                  ...(inProgressAccent?.showRunningGlow && {
                                    '@keyframes runnerTileRunningGlow': {
                                      '0%, 100%': {
                                        boxShadow: `0 0 8px ${alpha(theme.palette.secondary.main, 0.45)}, 0 2px 8px rgba(0,0,0,0.15)`,
                                      },
                                      '50%': {
                                        boxShadow: `0 0 18px ${alpha(theme.palette.secondary.main, 0.85)}, 0 2px 8px rgba(0,0,0,0.15)`,
                                      },
                                    },
                                    animation: 'runnerTileRunningGlow 2s ease-in-out infinite',
                                  }),
                                  '&:hover .runner-tile-overlay': { height: 130 },
                                  '&:hover .runner-tile-details': { opacity: 1 },
                                  '&:hover .runner-tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                                  '@media (hover: none)': {
                                    '& .runner-tile-overlay': { height: 130 },
                                    '& .runner-tile-details': { opacity: 1 },
                                    '& .runner-tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                                  },
                                }}
                              >
                                {/* Gradient background with icon and campaign name */}
                                <Box sx={{
                                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                  background: theme.palette.dashboard?.counterHeroGradient
                                    ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`,
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                                  ...(heroTileFg ? {
                                    color: heroTileFg,
                                    '& .MuiTypography-root': { color: 'inherit', textShadow: 'none' },
                                  } : {}),
                                }}>
                                  {tab.id === 'scheduled' && (
                                    <CountdownTimer targetTimestamp={campaign.startTimestamp} />
                                  )}
                                  {tab.id === 'in_progress' && (
                                    <CountdownTimer targetTimestamp={campaign.startTimestamp} mode="elapsed" />
                                  )}
                                  {tab.id === 'completed' && (
                                    <>
                                      <TaskAltIcon sx={{ fontSize: 40, color: heroTileFg ?? '#fff', opacity: 0.85 }} />
                                      <Typography sx={{
                                        fontSize: '0.78rem', fontWeight: 600, color: heroTileLabelColor,
                                        textShadow: heroTileTextShadow, textAlign: 'center',
                                      }}>
                                        {campaign.updatedAt ? formatUtcToLocal(campaign.updatedAt) : '—'}
                                      </Typography>
                                    </>
                                  )}
                                </Box>

                                {/* Top chips: Type, Status */}
                                <Box sx={{
                                  position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2,
                                  display: 'flex', gap: 0.5,
                                  '& .MuiChip-outlined': {
                                    backgroundColor: 'rgba(0,0,0,0.35)',
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    color: '#fff',
                                    '& .MuiChip-icon': { color: '#fff' },
                                  }
                                }}>
                                  {getCampaignTypeBadge(campaign.campaignType)}
                                  {getStatusChip(campaign.status)}
                                </Box>

                                {/* Bottom overlay — slides up on hover */}
                                <Box
                                  className="runner-tile-overlay"
                                  sx={{
                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                    overflow: 'hidden',
                                    backgroundColor: '#111827',
                                    transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                    height: 40, px: 1.5,
                                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                                  }}
                                >
                                  {/* Campaign name — truncated in default state */}
                                  <Typography variant="subtitle2" noWrap className="runner-tile-name-short" sx={{
                                    color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                                    lineHeight: '40px', flexShrink: 0,
                                    transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                                  }}>
                                    {campaign.campaignName || '—'}
                                  </Typography>

                                  {/* Details revealed on hover */}
                                  <Box className="runner-tile-details" sx={{
                                    opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                                    display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
                                  }}>
                                    <Typography variant="subtitle2" sx={{
                                      color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3,
                                      wordBreak: 'break-word',
                                    }}>
                                      {campaign.campaignName || '—'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                                      Scheduled: {formatUtcToLocal(campaign.startTimestamp)}
                                    </Typography>
                                    {campaign.templateName && (
                                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                                        Template: {campaign.templateName}
                                      </Typography>
                                    )}
                                    {/* Action buttons: Delete on right, rest on left */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {actions.filter(a => a.color !== 'error').map((action, idx) => (
                                          <Tooltip key={idx} title={action.label}>
                                            <Button
                                              variant="contained"
                                              color={action.color as any}
                                              size="small"
                                              onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                                              sx={{ borderRadius: 1, textTransform: 'none', px: 1, minWidth: 34, fontSize: '0.7rem' }}
                                            >
                                              {action.icon}
                                            </Button>
                                          </Tooltip>
                                        ))}
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {actions.filter(a => a.color === 'error').map((action, idx) => (
                                          <Tooltip key={idx} title={action.label}>
                                            <Button
                                              variant="contained"
                                              color={action.color as any}
                                              size="small"
                                              onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                                              sx={{ borderRadius: 1, textTransform: 'none', px: 1, minWidth: 34, fontSize: '0.7rem' }}
                                            >
                                              {action.icon}
                                            </Button>
                                          </Tooltip>
                                        ))}
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                        {visibleCount < filtered.length && (
                          <Box display="flex" justifyContent="center" mt={3}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => setTileVisibleCounts(prev => ({ ...prev, [tab.id]: (prev[tab.id] || TILE_PAGE_SIZE) + TILE_PAGE_SIZE }))}
                            >
                              Load More ({filtered.length - visibleCount} remaining)
                            </Button>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </>
                )}
              </Box>
            </Box>
          </Box>
        ) : null
      ))}

      {/* Wizard Dialog */}
      <Dialog 
        open={wizardOpen} 
        onClose={handleWizardClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobile ? 0 : 1,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: isMobile ? 1 : 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant={isMobile ? "h5" : "h6"}>
              {isEditing 
                ? (labels?.['modules-instantupdate-runner-new-dialogbox-edit-title'] ?? 'Edit Campaign') 
                : (labels?.['modules-instantupdate-runner-new-dialogbox-create-title'] ?? 'Create New Campaign')}
            </Typography>
            {isMobile && (
              <Button
                onClick={handleWizardClose}
                size="small"
                variant="contained"
                color="error"
                sx={{ minWidth: 'auto', px: 1, py: 0.5, borderRadius: 1.5 }}
              >
                <CancelIcon fontSize="small" />
              </Button>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {/* Mobile Step Indicator */}
          {isMobile && (
            <Box sx={{ mb: 3, mt: 1.25 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="body2" color="textSecondary">
                  {t('modules-instantupdate-runner-mobile-step-label', 'Step')} {activeStep + 1} {t('modules-instantupdate-runner-mobile-step-of', 'of')} {stepsLabels.length}
                </Typography>
                <Typography variant="body2" color="secondary.main" fontWeight="bold">
                  {stepsLabels[activeStep]}
                </Typography>
              </Box>
              
              {/* Progress Bar */}
              <Box sx={{ 
                width: '100%', 
                height: 4, 
                bgcolor: 'grey.200', 
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  width: `${((activeStep + 1) / steps.length) * 100}%`,
                  height: '100%',
                  bgcolor: 'success.main',
                  transition: 'width 0.3s ease'
                }} />
              </Box>
              
              {/* Step Dots */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 1, 
                mt: 1 
              }}>
                {steps.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                      transition: 'background-color 0.3s ease'
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Desktop Stepper */}
          {!isMobile && (
            <Stepper 
              activeStep={activeStep} 
              sx={{ 
                mb: 4, 
                mt: 2,
                '& .MuiStepIcon-root': {
                  color: theme.palette.grey[400],
                },
                '& .MuiStepIcon-root.Mui-active': {
                  color: theme.palette.primary.main,
                },
                '& .MuiStepIcon-root.Mui-completed': {
                  color: theme.palette.secondary.main,
                },
              }}
            >
              {stepsLabels.map((label, index) => (
                <Step key={label} disabled={index === 3 && !isWhatsAppTemplate}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          
          {/* Step Content */}
          <Box sx={{ 
            minHeight: isMobile ? '60vh' : 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {renderWizardStep(activeStep)}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: isMobile ? 2 : 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1
        }}>
          {!isMobile && (
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleWizardClose}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          )}
          {(!isMobile || activeStep > 0) && (
            <Button 
              disabled={activeStep === 0} 
              onClick={handleWizardBack}
              variant="outlined"
              color="secondary"
              fullWidth={isMobile}
            >
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button 
              onClick={handleCreateCampaign}
              variant="contained"
              color="success"
              disabled={loading}
              startIcon={<AddIcon />}
              fullWidth={isMobile}
            >
              {isEditing ? t('modules-instantupdate-runner-button-update', 'Update') : t('modules-instantupdate-runner-button-submit', 'Submit')}
            </Button>
          ) : (
            <Button 
              onClick={handleWizardNext}
              variant="contained"
              color="success"
              disabled={!isStepValid(activeStep)}
              fullWidth={isMobile}
            >
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description" mt={2}>
            Are you sure you want to delete campaign "{campaignToDelete?.campaignName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialogOpen(false)} 
            variant="contained" 
            color="secondary" 
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteCampaign} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stop Confirmation Dialog */}
      <Dialog
        open={stopConfirmDialogOpen}
        onClose={() => setStopConfirmDialogOpen(false)}
        aria-labelledby="stop-dialog-title"
        aria-describedby="stop-dialog-description"
      >
        <DialogTitle id="stop-dialog-title">
          {t('modules-instantupdate-runner-stop-dialog-title', 'Confirm Stop Campaign')}
        </DialogTitle>
        <DialogContent>
          <Typography id="stop-dialog-description" mt={2}>
            {t('modules-instantupdate-runner-stop-dialog-description-prefix', 'Are you sure you want to stop campaign')}
            {` "${campaignToStop?.campaignName || ''}"? `}
            {t('modules-instantupdate-runner-stop-dialog-description-suffix', 'This action cannot be undone. Once stopped, this campaign cannot be restarted and you will need to start a new campaign if you wish to continue.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setStopConfirmDialogOpen(false)} 
            variant="contained" 
            color="secondary" 
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmStopCampaign} 
            color="warning" 
            variant="contained"
            disabled={loading}
            startIcon={<StopIcon />}
          >
            {t('modules-instantupdate-runner-stop-dialog-stop-button', 'Stop')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 